import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, Upload, MousePointer2, Pencil, Trash2, ZoomIn, ZoomOut, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Minus, Plus, Ruler, Move, Eye, EyeOff, Target, CheckCircle2, AlertCircle } from 'lucide-react';
import * as pdfjsLib from 'pdfjs-dist';

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;

const TOOLS = { SELECT: 'select', ZONE: 'zone', WALL: 'wall', CALIBRATE: 'calibrate', PAN: 'pan', RULER: 'ruler' };
const OPENING_TYPES = [
  { id: 'door', label: 'Двері', defaultHeight: 2100 },
  { id: 'window', label: 'Вікно', defaultHeight: 1400 },
  { id: 'niche', label: 'Ніша', defaultHeight: 0 },
];
const WALL_TAGS = [
  { id: 'existing', label: 'Існуюча', color: '#6b7280' },
  { id: 'demolition', label: 'Демонтаж', color: '#ef4444' },
  { id: 'construction', label: 'Монтаж', color: '#22c55e' },
];
const ZONE_COLORS = ['#3b82f6', '#22c55e', '#f97316', '#a855f7', '#ec4899', '#14b8a6', '#eab308', '#ef4444'];
const BRAND = { navy: '#163248', orange: '#f97316' };

export default function DrawingReader({ onClose }) {
  // PDF state
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pageNum, setPageNum] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [scale, setScale] = useState(1.5);

  // Grid state — all stored at scale=1 (PDF coordinates)
  const [showGrid, setShowGrid] = useState(false);
  const [ppmBase, setPpmBase] = useState(0); // pixels per meter at scale=1
  const [gridOriginPdf, setGridOriginPdf] = useState({ x: 0, y: 0 }); // origin in PDF coords
  const [calibrated, setCalibrated] = useState(false);

  // Per-page grid origin overrides — { pageNum: {x,y} in PDF coords }
  const [pageOrigins, setPageOrigins] = useState({});

  // Derived values at current scale
  const pixelsPerMeter = ppmBase * scale;
  const gridStep = pixelsPerMeter / 100; // 1cm in current pixels
  const gridOriginEff = pageOrigins[pageNum] || gridOriginPdf;
  const gridOrigin = { x: gridOriginEff.x * scale, y: gridOriginEff.y * scale };

  // Calibration state
  const [calibrationStep, setCalibrationStep] = useState(0); // 0=not started, 1=first point, 2=second point, 3=entering distance
  const [calibrationPoints, setCalibrationPoints] = useState([]);
  const [calibrationDistance, setCalibrationDistance] = useState('');

  // Zone state
  const [zones, setZones] = useState([]);
  const [selectedZoneId, setSelectedZoneId] = useState(null);
  const [zoneNameEdit, setZoneNameEdit] = useState(null);
  const [hoveredSnapPoint, setHoveredSnapPoint] = useState(null);

  // Rectangle zone tool: first corner (PDF coords) + live mouse position
  const [rectFirstCorner, setRectFirstCorner] = useState(null);
  const [rectLiveCorner, setRectLiveCorner] = useState(null);

  // Vertex editing state
  const [draggingPointIdx, setDraggingPointIdx] = useState(null);
  const [pointContextMenu, setPointContextMenu] = useState(null); // { zoneId, pointIdx, x, y }
  const [originContextMenu, setOriginContextMenu] = useState(null); // { x, y } pixel coords
  const [movingOrigin, setMovingOrigin] = useState(false);
  const [alignGuides, setAlignGuides] = useState([]); // [{ axis:'h'|'v', pos }]

  // Ruler tool state — points in PDF coords
  const [rulerPoints, setRulerPoints] = useState([]); // fixed clicked points
  const [rulerCursor, setRulerCursor] = useState(null); // live cursor PDF coord
  const [showRulerSaveDialog, setShowRulerSaveDialog] = useState(false);
  const [rulerNotes, setRulerNotes] = useState([]); // saved measurements

  // Calibration axis snap
  const [calibAxisSnap, setCalibAxisSnap] = useState(null);

  // Edge highlight
  const [highlightedEdge, setHighlightedEdge] = useState(null); // { zoneId, edgeIdx }

  // Wall state
  const [walls, setWalls] = useState([]);
  const [selectedWallId, setSelectedWallId] = useState(null);
  const [wallFirstPoint, setWallFirstPoint] = useState(null); // PDF coords
  const [wallLivePoint, setWallLivePoint] = useState(null); // PDF coords
  const [draggingWallPoint, setDraggingWallPoint] = useState(null); // { wallId, point: 'p1'|'p2' }
  const [showWallOpeningForm, setShowWallOpeningForm] = useState(null); // { wallId }

  // Opening form (for zones and walls)
  const [showOpeningForm, setShowOpeningForm] = useState(null);
  const [draggingOpening, setDraggingOpening] = useState(null); // {zoneId, openingIdx} // {target:'zone'|'wall', id, edgeLabel?, type, widthMm, heightMm}

  // Tool state
  const [activeTool, setActiveTool] = useState(TOOLS.SELECT);

  // Pan/zoom state
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });

  // Refs
  const canvasRef = useRef(null);
  const overlayCanvasRef = useRef(null);
  const containerRef = useRef(null);
  const fileInputRef = useRef(null);

  // Load PDF
  const loadPDF = async (file) => {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    setPdfDoc(pdf);
    setTotalPages(pdf.numPages);
    setPageNum(1);
    setZones([]);
    setRectFirstCorner(null);
    setRectLiveCorner(null);
    setDraggingPointIdx(null);
    setPointContextMenu(null);
    setAlignGuides([]);
    setCalibrated(false);
    setShowGrid(false);
    setCalibrationStep(0);
    setCalibrationPoints([]);
    setCalibAxisSnap(null);
    setActiveTool(TOOLS.CALIBRATE);
    setCalibrationStep(1);
  };

  // Render PDF page
  const renderPage = useCallback(async () => {
    if (!pdfDoc || !canvasRef.current) return;
    const page = await pdfDoc.getPage(pageNum);
    const viewport = page.getViewport({ scale });
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    if (overlayCanvasRef.current) {
      overlayCanvasRef.current.width = viewport.width;
      overlayCanvasRef.current.height = viewport.height;
    }
    await page.render({ canvasContext: ctx, viewport }).promise;
    drawOverlay();
  }, [pdfDoc, pageNum, scale]);

  useEffect(() => { renderPage(); }, [renderPage]);

  // Snap point to grid (1cm precision)
  const snapToGrid = (x, y) => {
    if (!calibrated || gridStep <= 0) return { x, y };
    const relX = x - gridOrigin.x;
    const relY = y - gridOrigin.y;
    const snappedX = Math.round(relX / gridStep) * gridStep + gridOrigin.x;
    const snappedY = Math.round(relY / gridStep) * gridStep + gridOrigin.y;
    return { x: snappedX, y: snappedY };
  };

  // Draw overlay
  const drawOverlay = useCallback(() => {
    const canvas = overlayCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw grid — adaptive visibility
    // gridStep = 1cm in pixels (may be very small e.g. 0.5-3px)
    // We draw lines at visible intervals: 10cm, 50cm, 1m
    if (showGrid && gridStep > 0 && pixelsPerMeter > 0) {
      const cm10 = gridStep * 10;   // 10cm in pixels
      const cm50 = gridStep * 50;   // 50cm in pixels
      const m1 = gridStep * 100;    // 1m in pixels

      // Choose smallest visible step (at least 8px apart)
      let thinStep = cm10;
      let boldStep = cm50;
      if (cm10 < 8) { thinStep = cm50; boldStep = m1; }
      if (cm50 < 8) { thinStep = m1; boldStep = m1 * 5; }

      // Thin lines
      if (thinStep >= 4) {
        ctx.strokeStyle = 'rgba(59, 130, 246, 0.2)';
        ctx.lineWidth = 0.5;
        const ox = ((gridOrigin.x % thinStep) + thinStep) % thinStep;
        const oy = ((gridOrigin.y % thinStep) + thinStep) % thinStep;
        for (let x = ox; x < canvas.width; x += thinStep) {
          ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, canvas.height); ctx.stroke();
        }
        for (let y = oy; y < canvas.height; y += thinStep) {
          ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(canvas.width, y); ctx.stroke();
        }
      }

      // Bold lines
      if (boldStep >= 8) {
        ctx.strokeStyle = 'rgba(59, 130, 246, 0.45)';
        ctx.lineWidth = 1;
        const obx = ((gridOrigin.x % boldStep) + boldStep) % boldStep;
        const oby = ((gridOrigin.y % boldStep) + boldStep) % boldStep;
        for (let x = obx; x < canvas.width; x += boldStep) {
          ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, canvas.height); ctx.stroke();
        }
        for (let y = oby; y < canvas.height; y += boldStep) {
          ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(canvas.width, y); ctx.stroke();
        }

        // Dimension labels on bold lines (meters from origin)
        ctx.font = '10px Roboto, sans-serif';
        ctx.fillStyle = 'rgba(59, 130, 246, 0.6)';
        ctx.textAlign = 'left';
        for (let x = obx; x < canvas.width; x += boldStep) {
          const meters = (x - gridOrigin.x) / pixelsPerMeter;
          if (Math.abs(meters) > 0.01) {
            ctx.fillText(meters.toFixed(2) + 'м', x + 3, gridOrigin.y > 20 ? gridOrigin.y - 5 : 12);
          }
        }
      }

      // Extra bold lines every 1m if visible
      if (m1 >= 15) {
        ctx.strokeStyle = 'rgba(22, 50, 72, 0.3)';
        ctx.lineWidth = 1.5;
        const omx = ((gridOrigin.x % m1) + m1) % m1;
        const omy = ((gridOrigin.y % m1) + m1) % m1;
        for (let x = omx; x < canvas.width; x += m1) {
          ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, canvas.height); ctx.stroke();
        }
        for (let y = omy; y < canvas.height; y += m1) {
          ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(canvas.width, y); ctx.stroke();
        }
      }

      // Dots at 10cm intersections if visible
      if (cm10 >= 6) {
        ctx.fillStyle = 'rgba(59, 130, 246, 0.5)';
        const odx = ((gridOrigin.x % cm10) + cm10) % cm10;
        const ody = ((gridOrigin.y % cm10) + cm10) % cm10;
        for (let x = odx; x < canvas.width; x += cm10) {
          for (let y = ody; y < canvas.height; y += cm10) {
            ctx.beginPath(); ctx.arc(x, y, 1.5, 0, Math.PI * 2); ctx.fill();
          }
        }
      }

      // Grid origin cross
      ctx.strokeStyle = BRAND.orange;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(gridOrigin.x - 12, gridOrigin.y);
      ctx.lineTo(gridOrigin.x + 12, gridOrigin.y);
      ctx.moveTo(gridOrigin.x, gridOrigin.y - 12);
      ctx.lineTo(gridOrigin.x, gridOrigin.y + 12);
      ctx.stroke();
    }

    // Draw completed zones (convert PDF coords to pixel coords)
    const toPixel = (p) => ({ x: p.x * scale, y: p.y * scale });
    zones.filter(z => !z.page || z.page === pageNum).forEach((zone, idx) => {
      const color = ZONE_COLORS[idx % ZONE_COLORS.length];
      const pxPoints = zone.points.map(toPixel);
      drawZonePolygon(ctx, pxPoints, color, zone.id === selectedZoneId, zone.name);
    });

    // Draw wall rectangles from zone edges (where thickness is set)
    zones.filter(z => (!z.page || z.page === pageNum) && z.points.length >= 3).forEach(zone => {
      zone.points.forEach((p, i) => {
        const thick = zone.edgeThickness?.[i];
        if (!thick || thick <= 0) return;
        // Skip shared edges — wall already drawn by source zone
        if (zone.edgeShared?.[i]) return;
        const j = (i + 1) % zone.points.length;
        const a = toPixel(zone.points[i]), b = toPixel(zone.points[j]);
        const dx = b.x - a.x, dy = b.y - a.y;
        const len = Math.sqrt(dx * dx + dy * dy);
        if (len === 0) return;
        const thickPx = thick / 1000 * pixelsPerMeter;
        const edgeLenMm = ppmBase > 0 ? Math.sqrt((zone.points[j].x - zone.points[i].x) ** 2 + (zone.points[j].y - zone.points[i].y) ** 2) / ppmBase * 1000 : 0;
        // Outward from zone center
        const cx = zone.points.reduce((s, pt) => s + pt.x, 0) / zone.points.length;
        const cy = zone.points.reduce((s, pt) => s + pt.y, 0) / zone.points.length;
        const edgeMidX = (zone.points[i].x + zone.points[j].x) / 2;
        const edgeMidY = (zone.points[i].y + zone.points[j].y) / 2;
        const outX = edgeMidX - cx, outY = edgeMidY - cy;
        const nx = -dy / len, ny = dx / len;
        const dot = nx * outX + ny * outY;
        const side = dot >= 0 ? 1 : -1;
        const wnx = nx * thickPx * side, wny = ny * thickPx * side;
        // Unit vectors along edge
        const ux = dx / len, uy = dy / len;
        // Tag color
        const tagId = zone.edgeTags?.[i];
        const tag = tagId ? WALL_TAGS.find(t => t.id === tagId) : null;
        const color = tag ? tag.color : '#9ca3af';
        const label = EDGE_LABELS[i] || String(i + 1);

        // Get openings on this edge
        const edgeOpenings = (zone.openings || []).filter(o => o.edgeLabel === label);

        if (edgeOpenings.length === 0) {
          // Solid wall
          ctx.fillStyle = color + '25'; ctx.strokeStyle = color + '60'; ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y);
          ctx.lineTo(b.x + wnx, b.y + wny); ctx.lineTo(a.x + wnx, a.y + wny);
          ctx.closePath(); ctx.fill(); ctx.stroke();
        } else {
          // Wall with openings — draw solid segments and opening gaps
          // Sort openings by position
          const sorted = [...edgeOpenings].sort((x, y) => (x.positionMm || 0) - (y.positionMm || 0));
          let cursor = 0; // mm from edge start
          sorted.forEach(op => {
            const pos = op.positionMm || 0;
            const opW = op.widthMm || 900;
            // Solid segment before opening
            if (pos > cursor) {
              const t1 = cursor / (edgeLenMm || 1), t2 = pos / (edgeLenMm || 1);
              ctx.fillStyle = color + '25'; ctx.strokeStyle = color + '60'; ctx.lineWidth = 1;
              ctx.beginPath();
              ctx.moveTo(a.x + ux * len * t1, a.y + uy * len * t1);
              ctx.lineTo(a.x + ux * len * t2, a.y + uy * len * t2);
              ctx.lineTo(a.x + ux * len * t2 + wnx, a.y + uy * len * t2 + wny);
              ctx.lineTo(a.x + ux * len * t1 + wnx, a.y + uy * len * t1 + wny);
              ctx.closePath(); ctx.fill(); ctx.stroke();
            }
            // Opening gap — lighter fill + cross lines
            const ot1 = pos / (edgeLenMm || 1), ot2 = (pos + opW) / (edgeLenMm || 1);
            ctx.fillStyle = color + '08'; ctx.strokeStyle = color + '40'; ctx.lineWidth = 0.5;
            const ox1 = a.x + ux * len * ot1, oy1 = a.y + uy * len * ot1;
            const ox2 = a.x + ux * len * ot2, oy2 = a.y + uy * len * ot2;
            ctx.beginPath();
            ctx.moveTo(ox1, oy1); ctx.lineTo(ox2, oy2);
            ctx.lineTo(ox2 + wnx, oy2 + wny); ctx.lineTo(ox1 + wnx, oy1 + wny);
            ctx.closePath(); ctx.fill();
            // Cross lines (perpendicular marks)
            ctx.strokeStyle = color + '80'; ctx.lineWidth = 1;
            ctx.beginPath(); ctx.moveTo(ox1, oy1); ctx.lineTo(ox1 + wnx, oy1 + wny); ctx.stroke();
            ctx.beginPath(); ctx.moveTo(ox2, oy2); ctx.lineTo(ox2 + wnx, oy2 + wny); ctx.stroke();
            // Drag points on reference line (edge)
            if (zone.id === selectedZoneId) {
              [{ x: ox1, y: oy1 }, { x: ox2, y: oy2 }].forEach(dp => {
                ctx.fillStyle = BRAND.orange; ctx.beginPath(); ctx.arc(dp.x, dp.y, 4, 0, Math.PI * 2); ctx.fill();
                ctx.strokeStyle = 'white'; ctx.lineWidth = 1.5; ctx.stroke();
              });
            }
            cursor = pos + opW;
          });
          // Solid segment after last opening
          if (cursor < edgeLenMm) {
            const t1 = cursor / (edgeLenMm || 1), t2 = 1;
            ctx.fillStyle = color + '25'; ctx.strokeStyle = color + '60'; ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(a.x + ux * len * t1, a.y + uy * len * t1);
            ctx.lineTo(a.x + ux * len * t2, a.y + uy * len * t2);
            ctx.lineTo(a.x + ux * len * t2 + wnx, a.y + uy * len * t2 + wny);
            ctx.lineTo(a.x + ux * len * t1 + wnx, a.y + uy * len * t1 + wny);
            ctx.closePath(); ctx.fill(); ctx.stroke();
          }
        }
      });
    });

    // Draw edit handles on selected zone (SELECT mode)
    if (selectedZoneId && activeTool === TOOLS.SELECT) {
      const zone = zones.find(z => z.id === selectedZoneId);
      if (zone) {
        const pts = zone.points.map(toPixel);
        // Vertex handles
        pts.forEach(p => {
          ctx.fillStyle = 'white'; ctx.strokeStyle = BRAND.navy; ctx.lineWidth = 2;
          ctx.beginPath(); ctx.arc(p.x, p.y, 6, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
        });
        // Midpoint handles (add point)
        for (let i = 0; i < pts.length; i++) {
          const j = (i + 1) % pts.length;
          const mx = (pts[i].x + pts[j].x) / 2, my = (pts[i].y + pts[j].y) / 2;
          ctx.fillStyle = BRAND.orange + '50'; ctx.strokeStyle = BRAND.orange; ctx.lineWidth = 1.5;
          ctx.beginPath(); ctx.arc(mx, my, 4, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
        }
      }
    }


    // Draw walls (current page only)
    walls.filter(w => w.page === pageNum).forEach(wall => {
      const pa = toPixel(wall.p1), pb = toPixel(wall.p2);
      const tag = WALL_TAGS.find(t => t.id === wall.tag) || WALL_TAGS[0];
      const isSel = wall.id === selectedWallId;
      const dx = pb.x - pa.x, dy = pb.y - pa.y;
      const len = Math.sqrt(dx * dx + dy * dy);
      if (len === 0) return;
      // Normal vector for thickness — one side only
      const thickPx = (wall.thicknessMm || 100) / 1000 * pixelsPerMeter;
      const s = wall.side || 1;
      const nx = -dy / len * thickPx * s, ny = dx / len * thickPx * s;
      // Rectangle corners: reference line (pa→pb) is one edge, offset is the other
      const corners = [
        { x: pa.x, y: pa.y }, { x: pb.x, y: pb.y },
        { x: pb.x + nx, y: pb.y + ny }, { x: pa.x + nx, y: pa.y + ny },
      ];
      // Fill rectangle
      ctx.fillStyle = tag.color + (isSel ? '50' : '30');
      ctx.strokeStyle = tag.color + (isSel ? 'cc' : '80');
      ctx.lineWidth = isSel ? 2 : 1;
      ctx.beginPath(); ctx.moveTo(corners[0].x, corners[0].y);
      corners.forEach((c, i) => { if (i > 0) ctx.lineTo(c.x, c.y); });
      ctx.closePath(); ctx.fill(); ctx.stroke();
      // Center line (прив'язка)
      ctx.strokeStyle = tag.color; ctx.lineWidth = isSel ? 2 : 1; ctx.setLineDash([4, 3]);
      ctx.beginPath(); ctx.moveTo(pa.x, pa.y); ctx.lineTo(pb.x, pb.y); ctx.stroke(); ctx.setLineDash([]);
      // Vertices
      if (isSel) {
        [pa, pb].forEach(p => { ctx.fillStyle = 'white'; ctx.strokeStyle = tag.color; ctx.lineWidth = 2; ctx.beginPath(); ctx.arc(p.x, p.y, 5, 0, Math.PI * 2); ctx.fill(); ctx.stroke(); });
      }
      // Length label
      const mx = (pa.x + pb.x) / 2, my = (pa.y + pb.y) / 2;
      const lenMm = ppmBase > 0 ? Math.round(Math.sqrt((wall.p2.x - wall.p1.x) ** 2 + (wall.p2.y - wall.p1.y) ** 2) / ppmBase * 1000) : 0;
      const tagLabel = wall.tag === 'demolition' ? 'Д' : wall.tag === 'construction' ? 'М' : 'І';
      ctx.font = 'bold 9px Roboto,sans-serif'; ctx.textAlign = 'center';
      ctx.fillStyle = tag.color; ctx.fillText(`${tagLabel} ${lenMm}`, mx, my - thickPx / 2 - 4);
    });

    // Wall live preview
    if (wallFirstPoint && wallLivePoint) {
      const pa = toPixel(wallFirstPoint), pb = toPixel(wallLivePoint);
      const dx = pb.x - pa.x, dy = pb.y - pa.y;
      const len = Math.sqrt(dx * dx + dy * dy);
      const thickPx = 100 / 1000 * pixelsPerMeter; // default 100mm
      if (len > 0) {
        const nx = -dy / len * thickPx, ny = dx / len * thickPx;
        ctx.fillStyle = BRAND.navy + '15'; ctx.strokeStyle = BRAND.navy + '60'; ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(pa.x, pa.y); ctx.lineTo(pb.x, pb.y);
        ctx.lineTo(pb.x + nx, pb.y + ny); ctx.lineTo(pa.x + nx, pa.y + ny);
        ctx.closePath(); ctx.fill(); ctx.stroke();
      }
      // Center line
      ctx.strokeStyle = BRAND.navy + '80'; ctx.lineWidth = 1.5; ctx.setLineDash([6, 4]);
      ctx.beginPath(); ctx.moveTo(pa.x, pa.y); ctx.lineTo(pb.x, pb.y); ctx.stroke(); ctx.setLineDash([]);
      // Length
      const lenMm = ppmBase > 0 ? Math.round(Math.sqrt((wallLivePoint.x - wallFirstPoint.x) ** 2 + (wallLivePoint.y - wallFirstPoint.y) ** 2) / ppmBase * 1000) : 0;
      ctx.font = 'bold 11px Roboto,sans-serif'; ctx.textAlign = 'center'; ctx.fillStyle = BRAND.navy;
      ctx.fillText(lenMm + ' мм', (pa.x + pb.x) / 2, (pa.y + pb.y) / 2 - thickPx / 2 - 6);
      // Start point
      ctx.fillStyle = BRAND.orange; ctx.beginPath(); ctx.arc(pa.x, pa.y, 4, 0, Math.PI * 2); ctx.fill();
    }


    // Highlighted edge
    if (highlightedEdge) {
      const zone = zones.find(z => z.id === highlightedEdge.zoneId);
      if (zone && zone.points.length > highlightedEdge.edgeIdx) {
        const i = highlightedEdge.edgeIdx;
        const j = (i + 1) % zone.points.length;
        const p1 = toPixel(zone.points[i]), p2 = toPixel(zone.points[j]);
        ctx.strokeStyle = BRAND.orange; ctx.lineWidth = 4;
        ctx.beginPath(); ctx.moveTo(p1.x, p1.y); ctx.lineTo(p2.x, p2.y); ctx.stroke();
        ctx.fillStyle = BRAND.orange;
        ctx.beginPath(); ctx.arc(p1.x, p1.y, 5, 0, Math.PI * 2); ctx.fill();
        ctx.beginPath(); ctx.arc(p2.x, p2.y, 5, 0, Math.PI * 2); ctx.fill();
        // Label
        const mx = (p1.x + p2.x) / 2, my = (p1.y + p2.y) / 2;
        const label = EDGE_LABELS[i] || String(i + 1);
        ctx.font = 'bold 14px Roboto, sans-serif'; ctx.textAlign = 'center';
        ctx.fillStyle = 'white'; ctx.fillText(label, mx, my - 8);
        ctx.fillStyle = BRAND.orange; ctx.fillText(label, mx - 1, my - 9);
      }
    }

    // Alignment guides
    alignGuides.forEach(g => {
      ctx.strokeStyle = '#22c55e'; ctx.lineWidth = 1; ctx.setLineDash([4, 4]);
      ctx.beginPath();
      if (g.axis === 'h') { ctx.moveTo(0, g.pos * scale); ctx.lineTo(canvas.width, g.pos * scale); }
      else { ctx.moveTo(g.pos * scale, 0); ctx.lineTo(g.pos * scale, canvas.height); }
      ctx.stroke(); ctx.setLineDash([]);
    });

    // Draw live rectangle preview
    if (rectFirstCorner && rectLiveCorner) {
      const c1 = toPixel(rectFirstCorner);
      const c2 = toPixel(rectLiveCorner);
      const color = ZONE_COLORS[zones.length % ZONE_COLORS.length];
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 4]);
      ctx.fillStyle = color + '15';
      const rx = Math.min(c1.x, c2.x), ry = Math.min(c1.y, c2.y);
      const rw = Math.abs(c2.x - c1.x), rh = Math.abs(c2.y - c1.y);
      ctx.fillRect(rx, ry, rw, rh);
      ctx.strokeRect(rx, ry, rw, rh);
      ctx.setLineDash([]);

      // Show dimensions on the rectangle
      if (ppmBase > 0) {
        const wM = Math.abs(rectLiveCorner.x - rectFirstCorner.x) / ppmBase;
        const hM = Math.abs(rectLiveCorner.y - rectFirstCorner.y) / ppmBase;
        ctx.font = 'bold 12px Roboto, sans-serif';
        ctx.fillStyle = color;
        ctx.textAlign = 'center';
        ctx.fillText(wM.toFixed(2) + ' м', rx + rw / 2, ry - 6);
        ctx.save();
        ctx.translate(rx - 6, ry + rh / 2);
        ctx.rotate(-Math.PI / 2);
        ctx.fillText(hM.toFixed(2) + ' м', 0, 0);
        ctx.restore();
        // Area preview
        ctx.fillStyle = 'white';
        const areaText = (wM * hM).toFixed(2) + ' м²';
        const am = ctx.measureText(areaText);
        ctx.fillStyle = color + 'cc';
        ctx.beginPath();
        ctx.roundRect(rx + rw / 2 - am.width / 2 - 6, ry + rh / 2 - 10, am.width + 12, 22, 4);
        ctx.fill();
        ctx.fillStyle = 'white';
        ctx.fillText(areaText, rx + rw / 2, ry + rh / 2 + 5);
      }

      // Corner dots
      [c1, { x: c2.x, y: c1.y }, c2, { x: c1.x, y: c2.y }].forEach(p => {
        ctx.fillStyle = color;
        ctx.beginPath(); ctx.arc(p.x, p.y, 5, 0, Math.PI * 2); ctx.fill();
        ctx.strokeStyle = 'white'; ctx.lineWidth = 2; ctx.stroke();
      });
    } else if (rectFirstCorner && !rectLiveCorner) {
      // Just first corner dot
      const c = toPixel(rectFirstCorner);
      ctx.fillStyle = BRAND.orange;
      ctx.beginPath(); ctx.arc(c.x, c.y, 6, 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = 'white'; ctx.lineWidth = 2; ctx.stroke();
    }

    // Hovered snap point
    if (hoveredSnapPoint && (activeTool === TOOLS.ZONE || activeTool === TOOLS.RULER || activeTool === TOOLS.WALL) && calibrated) {
      ctx.strokeStyle = BRAND.orange;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(hoveredSnapPoint.x - 6, hoveredSnapPoint.y);
      ctx.lineTo(hoveredSnapPoint.x + 6, hoveredSnapPoint.y);
      ctx.moveTo(hoveredSnapPoint.x, hoveredSnapPoint.y - 6);
      ctx.lineTo(hoveredSnapPoint.x, hoveredSnapPoint.y + 6);
      ctx.stroke();
    }

    // Ruler tool lines
    if (activeTool === TOOLS.RULER && rulerPoints.length > 0) {
      const allPts = [...rulerPoints];
      if (rulerCursor) allPts.push(rulerCursor);
      const pxPts = allPts.map(toPixel);
      // Draw lines
      ctx.strokeStyle = '#8b5cf6'; ctx.lineWidth = 2;
      for (let i = 0; i < pxPts.length - 1; i++) {
        ctx.beginPath(); ctx.moveTo(pxPts[i].x, pxPts[i].y); ctx.lineTo(pxPts[i + 1].x, pxPts[i + 1].y); ctx.stroke();
      }
      // Draw fill if 3+ points
      if (allPts.length >= 3) {
        ctx.fillStyle = 'rgba(139,92,246,0.1)';
        ctx.beginPath(); ctx.moveTo(pxPts[0].x, pxPts[0].y);
        pxPts.forEach((p, i) => { if (i > 0) ctx.lineTo(p.x, p.y); });
        ctx.closePath(); ctx.fill();
      }
      // Draw points
      pxPts.forEach((p, i) => {
        ctx.fillStyle = i < rulerPoints.length ? '#8b5cf6' : BRAND.orange;
        ctx.beginPath(); ctx.arc(p.x, p.y, 4, 0, Math.PI * 2); ctx.fill();
        ctx.strokeStyle = 'white'; ctx.lineWidth = 2; ctx.stroke();
      });
    }

    // Calibration points
    if (calibrationPoints.length > 0) {
      ctx.strokeStyle = '#ef4444';
      ctx.lineWidth = 2;
      calibrationPoints.forEach(p => {
        ctx.beginPath(); ctx.arc(p.x, p.y, 8, 0, Math.PI * 2); ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(p.x - 10, p.y); ctx.lineTo(p.x + 10, p.y);
        ctx.moveTo(p.x, p.y - 10); ctx.lineTo(p.x, p.y + 10);
        ctx.stroke();
      });
      if (calibrationPoints.length === 2) {
        ctx.setLineDash([6, 4]);
        ctx.strokeStyle = '#ef4444';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(calibrationPoints[0].x, calibrationPoints[0].y);
        ctx.lineTo(calibrationPoints[1].x, calibrationPoints[1].y);
        ctx.stroke();
        ctx.setLineDash([]);

        // Length label
        const mx = (calibrationPoints[0].x + calibrationPoints[1].x) / 2;
        const my = (calibrationPoints[0].y + calibrationPoints[1].y) / 2;
        const dx = calibrationPoints[1].x - calibrationPoints[0].x;
        const dy = calibrationPoints[1].y - calibrationPoints[0].y;
        const pxDist = Math.sqrt(dx * dx + dy * dy);
        ctx.font = 'bold 12px Roboto, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillStyle = '#ef4444';
        ctx.fillText(`${Math.round(pxDist)} px`, mx, my - 10);
      }

      // Axis guide line when placing second calibration point
      if (calibrationPoints.length === 1 && calibAxisSnap) {
        const p1 = calibrationPoints[0];
        ctx.strokeStyle = '#22c55e';
        ctx.lineWidth = 1;
        ctx.setLineDash([4, 4]);
        ctx.beginPath();
        if (calibAxisSnap === 'h') {
          ctx.moveTo(0, p1.y); ctx.lineTo(canvas.width, p1.y);
        } else {
          ctx.moveTo(p1.x, 0); ctx.lineTo(p1.x, canvas.height);
        }
        ctx.stroke();
        ctx.setLineDash([]);
        // Label
        ctx.font = 'bold 11px Roboto, sans-serif';
        ctx.fillStyle = '#22c55e';
        ctx.textAlign = 'left';
        ctx.fillText(calibAxisSnap === 'h' ? 'Горизонталь' : 'Вертикаль', p1.x + 15, p1.y - 8);
      }
    }
  }, [showGrid, gridStep, gridOrigin, zones, selectedZoneId, calibrationPoints, hoveredSnapPoint, activeTool, calibrated, pixelsPerMeter, rectFirstCorner, rectLiveCorner, calibAxisSnap, ppmBase, scale, alignGuides, rulerPoints, rulerCursor, highlightedEdge, walls, selectedWallId, wallFirstPoint, wallLivePoint, pageNum]);

  useEffect(() => { drawOverlay(); }, [drawOverlay]);

  // Draw zone polygon
  const drawZonePolygon = (ctx, points, color, isSelected, name) => {
    if (points.length < 3) return;
    ctx.fillStyle = isSelected ? color + '40' : color + '20';
    ctx.strokeStyle = isSelected ? color : color + 'aa';
    ctx.lineWidth = isSelected ? 3 : 2;
    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);
    points.forEach((p, i) => { if (i > 0) ctx.lineTo(p.x, p.y); });
    ctx.closePath(); ctx.fill(); ctx.stroke();

    points.forEach(p => {
      ctx.fillStyle = color;
      ctx.beginPath(); ctx.arc(p.x, p.y, 4, 0, Math.PI * 2); ctx.fill();
    });

    if (name) {
      const cx = points.reduce((s, p) => s + p.x, 0) / points.length;
      const cy = points.reduce((s, p) => s + p.y, 0) / points.length;
      ctx.font = 'bold 13px Roboto, sans-serif';
      ctx.textAlign = 'center';
      const metrics = ctx.measureText(name);
      const pad = 6;
      ctx.fillStyle = color + 'dd';
      ctx.beginPath();
      ctx.roundRect(cx - metrics.width / 2 - pad, cy - 9 - pad, metrics.width + pad * 2, 18 + pad, 4);
      ctx.fill();
      ctx.fillStyle = 'white';
      ctx.fillText(name, cx, cy + 4);
    }
  };

  // Calculate area (Shoelace) in m² — points are in PDF coords, use ppmBase
  const calcArea = (points) => {
    if (points.length < 3 || !ppmBase) return 0;
    let area = 0;
    for (let i = 0; i < points.length; i++) {
      const j = (i + 1) % points.length;
      area += points[i].x * points[j].y;
      area -= points[j].x * points[i].y;
    }
    return Math.abs(area) / 2 / (ppmBase * ppmBase);
  };

  // Calculate perimeter in meters — points are in PDF coords
  const calcPerimeter = (points) => {
    if (points.length < 2 || !ppmBase) return 0;
    let p = 0;
    for (let i = 0; i < points.length; i++) {
      const j = (i + 1) % points.length;
      const dx = points[j].x - points[i].x;
      const dy = points[j].y - points[i].y;
      p += Math.sqrt(dx * dx + dy * dy);
    }
    return p / ppmBase;
  };

  // Get edges of a zone with labels (A, B, C, D...)
  const EDGE_LABELS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L'];
  const WALL_MATERIALS = ['бетон', 'цегла', 'газоблок', 'піноблок', 'шлакоблок', 'ГКЛ', 'панель', 'дерево', 'інше'];
  const getZoneEdges = (points, height, openings) => {
    if (!points || points.length < 2 || !ppmBase) return [];
    return points.map((p, i) => {
      const j = (i + 1) % points.length;
      const dx = points[j].x - points[i].x;
      const dy = points[j].y - points[i].y;
      const lengthM = Math.sqrt(dx * dx + dy * dy) / ppmBase;
      const lengthMm = Math.round(lengthM * 1000);
      const h = height || 0;
      const label = EDGE_LABELS[i] || String(i + 1);
      // Subtract openings from wall area
      const edgeOpenings = (openings || []).filter(o => o.edgeLabel === label);
      const openingsArea = edgeOpenings.reduce((sum, o) => sum + (o.widthMm * o.heightMm) / 1000000, 0);
      const grossArea = h > 0 ? (lengthMm * h) / 1000000 : 0;
      const netArea = Math.max(0, grossArea - openingsArea);
      return {
        label,
        lengthMm,
        lengthM: lengthM.toFixed(2),
        wallArea: h > 0 ? netArea.toFixed(2) : null,
        grossWallArea: h > 0 ? grossArea.toFixed(2) : null,
        openingsArea: openingsArea > 0 ? openingsArea.toFixed(2) : null,
      };
    });
  };

  // Collect all wall edges (inner + outer) for snapping — in PDF coords
  const getAllWallEdges = () => {
    const edges = []; // [{p1, p2, zoneId?, wallId?, edgeIdx?, type:'inner'|'outer', material, thickness, tag}]
    // From zone edges with thickness
    zones.filter(z => (!z.page || z.page === pageNum) && z.points.length >= 3).forEach(zone => {
      const cx = zone.points.reduce((s, pt) => s + pt.x, 0) / zone.points.length;
      const cy = zone.points.reduce((s, pt) => s + pt.y, 0) / zone.points.length;
      zone.points.forEach((p, i) => {
        const thick = zone.edgeThickness?.[i];
        if (!thick || thick <= 0) return;
        if (zone.edgeShared?.[i]) return;
        const j = (i + 1) % zone.points.length;
        const a = zone.points[i], b = zone.points[j];
        const dx = b.x - a.x, dy = b.y - a.y;
        const len = Math.sqrt(dx * dx + dy * dy);
        if (len === 0) return;
        const thickPdf = thick / 1000 * ppmBase;
        const emx = (a.x + b.x) / 2, emy = (a.y + b.y) / 2;
        const nx = -dy / len, ny = dx / len;
        const dot = nx * (emx - cx) + ny * (emy - cy);
        const side = dot >= 0 ? 1 : -1;
        const onx = nx * thickPdf * side, ony = ny * thickPdf * side;
        // Inner edge = zone edge itself
        edges.push({ p1: a, p2: b, zoneId: zone.id, edgeIdx: i, type: 'inner', material: zone.edgeMaterials?.[i], thickness: thick, tag: zone.edgeTags?.[i] });
        // Outer edge = offset
        edges.push({ p1: { x: a.x + onx, y: a.y + ony }, p2: { x: b.x + onx, y: b.y + ony }, zoneId: zone.id, edgeIdx: i, type: 'outer', material: zone.edgeMaterials?.[i], thickness: thick, tag: zone.edgeTags?.[i] });
      });
    });
    // From standalone walls
    walls.filter(w => w.page === pageNum).forEach(w => {
      const dx = w.p2.x - w.p1.x, dy = w.p2.y - w.p1.y;
      const len = Math.sqrt(dx * dx + dy * dy);
      if (len === 0) return;
      const thickPdf = (w.thicknessMm || 100) / 1000 * ppmBase;
      const s = w.side || 1;
      const nx = -dy / len * thickPdf * s, ny = dx / len * thickPdf * s;
      edges.push({ p1: w.p1, p2: w.p2, wallId: w.id, type: 'inner', material: w.material, thickness: w.thicknessMm, tag: w.tag });
      edges.push({ p1: { x: w.p1.x + nx, y: w.p1.y + ny }, p2: { x: w.p2.x + nx, y: w.p2.y + ny }, wallId: w.id, type: 'outer', material: w.material, thickness: w.thicknessMm, tag: w.tag });
    });
    return edges;
  };

  // Distance between two PDF points in meters
  const pdfDist = (a, b) => ppmBase ? Math.sqrt((b.x - a.x) ** 2 + (b.y - a.y) ** 2) / ppmBase : 0;

  // Find nearest edge to a point (in pixel coords), returns {zoneId, edgeIdx, projPoint (pdf coords), dist}
  const findNearestEdge = (px, py) => {
    const pdfX = px / scale, pdfY = py / scale;
    let best = null;
    zones.forEach(zone => {
      if (zone.points.length < 3) return;
      zone.points.forEach((p, i) => {
        const j = (i + 1) % zone.points.length;
        const a = p, b = zone.points[j];
        const dx = b.x - a.x, dy = b.y - a.y;
        const lenSq = dx * dx + dy * dy;
        if (lenSq === 0) return;
        let t = Math.max(0, Math.min(1, ((pdfX - a.x) * dx + (pdfY - a.y) * dy) / lenSq));
        const projX = a.x + t * dx, projY = a.y + t * dy;
        const dist = Math.sqrt((pdfX - projX) ** 2 + (pdfY - projY) ** 2) * scale;
        if (!best || dist < best.dist) {
          best = { zoneId: zone.id, edgeIdx: i, projPoint: { x: projX, y: projY }, t, dist };
        }
      });
    });
    return best;
  };

  // Handle canvas click
  const handleCanvasClick = (e) => {
    const rect = overlayCanvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Moving origin — click to place
    if (movingOrigin && calibrated) {
      const snapped = snapToGrid(x, y);
      const newOrigin = { x: snapped.x / scale, y: snapped.y / scale };
      setPageOrigins(prev => ({ ...prev, [pageNum]: newOrigin }));
      setMovingOrigin(false);
      return;
    }

    // Calibration — snap second point to horizontal/vertical axis
    if (activeTool === TOOLS.CALIBRATE) {
      if (calibrationPoints.length === 0) {
        setCalibrationPoints([{ x, y }]);
        setCalibrationStep(2);
      } else if (calibrationPoints.length === 1) {
        const p1 = calibrationPoints[0];
        let fx = x, fy = y;
        const dx = Math.abs(x - p1.x), dy = Math.abs(y - p1.y);
        // Snap to axis if within 15° angle
        const angle = Math.atan2(dy, dx) * 180 / Math.PI;
        if (angle < 15) { fy = p1.y; } // horizontal
        else if (angle > 75) { fx = p1.x; } // vertical
        setCalibrationPoints([p1, { x: fx, y: fy }]);
        setCalibrationStep(3);
        setCalibAxisSnap(null);
      }
      return;
    }

    // Zone drawing — rectangle mode: first click = corner1, second click = corner2 → rect
    if (activeTool === TOOLS.ZONE && calibrated) {
      const snapped = snapToGrid(x, y);
      const pdfPoint = { x: snapped.x / scale, y: snapped.y / scale };

      if (!rectFirstCorner) {
        // First corner
        setRectFirstCorner(pdfPoint);
      } else {
        // Second corner → create rectangle zone (4 points CW)
        const c1 = rectFirstCorner;
        const c2 = pdfPoint;
        const rectPoints = [
          { x: c1.x, y: c1.y },
          { x: c2.x, y: c1.y },
          { x: c2.x, y: c2.y },
          { x: c1.x, y: c2.y },
        ];

        const pageZones = zones.filter(z => z.page === pageNum);

        // Split edges by walls (partial coverage)
        const split = splitEdgesByWalls(rectPoints);

        const newZone = {
          id: Date.now(),
          page: pageNum,
          name: `Кімната ${pageZones.length + 1}`,
          points: split.points,
          height: 0,
          edgeMaterials: split.edgeMaterials,
          edgeTags: split.edgeTags,
          edgeThickness: split.edgeThickness,
        };

        // Also check full shared edges (from other zones)
        const shared = findSharedEdges(split.points);
        if (shared.length > 0) {
          shared.forEach(s => {
            if (s.material && !newZone.edgeMaterials[s.newEdgeIdx]) newZone.edgeMaterials[s.newEdgeIdx] = s.material;
            if (s.tag && !newZone.edgeTags[s.newEdgeIdx]) newZone.edgeTags[s.newEdgeIdx] = s.tag;
            if (s.thickness && !newZone.edgeThickness[s.newEdgeIdx]) newZone.edgeThickness[s.newEdgeIdx] = s.thickness;
          });
          const srcZone = zones.find(z => z.id === shared[0].existingZoneId);
          if (srcZone?.height) newZone.height = srcZone.height;
        }

        if (shared.length > 0 || split.modified) {
          setSharedEdgePrompts(shared.length > 0 ? shared : [{ info: 'Ребра розділені по стінах' }]);
          setPendingNewZone(newZone);
        } else {
          setZones([...zones, newZone]);
          setSelectedZoneId(newZone.id);
        }
        setRectFirstCorner(null);
        setRectLiveCorner(null);
      }
      return;
    }

    // Wall tool — two clicks, then form (use same snap as mousemove)
    if (activeTool === TOOLS.WALL && calibrated) {
      // Use hoveredSnapPoint which already has wall/guide snapping applied
      const pdfPt = wallLivePoint || { x: (hoveredSnapPoint || snapToGrid(x, y)).x / scale, y: (hoveredSnapPoint || snapToGrid(x, y)).y / scale };
      if (!wallFirstPoint) {
        setWallFirstPoint(pdfPt);
        setAlignGuides([]);
      } else {
        setShowWallOpeningForm({
          isNew: true, p1: wallFirstPoint, p2: pdfPt,
          tag: 'existing', heightMm: 2700, slopedWall: false, heightP1Mm: 2700, heightP2Mm: 2700, material: '', thicknessMm: 100, side: 1, openings: [],
        });
        setWallFirstPoint(null); setWallLivePoint(null); setAlignGuides([]);
      }
      return;
    }

    // Ruler tool — add point
    if (activeTool === TOOLS.RULER && calibrated) {
      const snapped = snapToGrid(x, y);
      const pdfPt = { x: snapped.x / scale, y: snapped.y / scale };
      setRulerPoints([...rulerPoints, pdfPt]);
      return;
    }

    // Select mode
    if (activeTool === TOOLS.SELECT) {
      setPointContextMenu(null);

      // If zone is selected — check midpoint click to add point
      if (selectedZoneId) {
        const zone = zones.find(z => z.id === selectedZoneId);
        if (zone) {
          // Check vertex click — skip (handled by mousedown for drag)
          for (let i = 0; i < zone.points.length; i++) {
            const vp = zone.points[i];
            if (Math.sqrt((x - vp.x * scale) ** 2 + (y - vp.y * scale) ** 2) < 10) return;
          }
          // Check midpoint click → insert point
          for (let i = 0; i < zone.points.length; i++) {
            const j = (i + 1) % zone.points.length;
            const a = zone.points[i], b = zone.points[j];
            const mpx = (a.x + b.x) / 2 * scale, mpy = (a.y + b.y) / 2 * scale;
            if (Math.sqrt((x - mpx) ** 2 + (y - mpy) ** 2) < 8) {
              const snapped = snapToGrid(mpx, mpy);
              const newPts = [...zone.points];
              newPts.splice(j, 0, { x: snapped.x / scale, y: snapped.y / scale });
              // Shift edge params: indices >= j move up by 1, edge i keeps its params
              const shiftParams = (obj) => {
                if (!obj) return {};
                const shifted = {};
                Object.entries(obj).forEach(([k, v]) => {
                  const idx = parseInt(k);
                  shifted[idx >= j ? idx + 1 : idx] = v;
                });
                return shifted;
              };
              // Shift opening edgeLabels
              const shiftOpenings = (openings) => {
                if (!openings) return [];
                return openings.map(op => {
                  const oIdx = EDGE_LABELS.indexOf(op.edgeLabel);
                  if (oIdx >= j) return { ...op, edgeLabel: EDGE_LABELS[oIdx + 1] || String(oIdx + 2) };
                  return op;
                });
              };
              setZones(zones.map(z => z.id !== selectedZoneId ? z : {
                ...z, points: newPts,
                edgeMaterials: shiftParams(z.edgeMaterials),
                edgeTags: shiftParams(z.edgeTags),
                edgeThickness: shiftParams(z.edgeThickness),
                edgeShared: shiftParams(z.edgeShared),
                openings: shiftOpenings(z.openings),
              }));
              return;
            }
          }
        }
      }

      // Select zone by clicking inside
      const px = x / scale, py = y / scale;
      let foundZone = null;
      for (let i = zones.length - 1; i >= 0; i--) {
        if (isPointInPolygon(px, py, zones[i].points)) { foundZone = zones[i].id; break; }
      }

      // Select wall or flip if clicking on rectangle of already selected wall
      let foundWall = null;
      if (!foundZone) {
        const pageWalls = walls.filter(w => w.page === pageNum);
        pageWalls.forEach(w => {
          const wdx = w.p2.x - w.p1.x, wdy = w.p2.y - w.p1.y;
          const wlen = Math.sqrt(wdx * wdx + wdy * wdy);
          if (wlen === 0) return;
          // Check if point is inside wall rectangle
          const thickM = (w.thicknessMm || 100) / 1000;
          const s = w.side || 1;
          const wnx = -wdy / wlen * thickM * ppmBase * s, wny = wdx / wlen * thickM * ppmBase * s;
          const rectPts = [
            w.p1, w.p2,
            { x: w.p2.x + wnx, y: w.p2.y + wny },
            { x: w.p1.x + wnx, y: w.p1.y + wny },
          ];
          if (isPointInPolygon(px, py, rectPts)) {
            if (w.id === selectedWallId) {
              // Already selected — flip side
              setWalls(walls.map(ww => ww.id === w.id ? { ...ww, side: (ww.side || 1) * -1 } : ww));
              foundWall = { id: w.id };
            } else {
              foundWall = { id: w.id };
            }
          }
          // Also check proximity to center line
          if (!foundWall) {
            const t = Math.max(0, Math.min(1, ((px - w.p1.x) * wdx + (py - w.p1.y) * wdy) / (wlen * wlen)));
            const projX = w.p1.x + t * wdx, projY = w.p1.y + t * wdy;
            const dist = Math.sqrt((px - projX) ** 2 + (py - projY) ** 2) * scale;
            if (dist < 12) foundWall = { id: w.id };
          }
        });
      }

      setSelectedZoneId(foundZone);
      setSelectedWallId(foundWall ? foundWall.id : null);
      if (!foundZone) setHighlightedEdge(null);
    }
  };

  // Overlay mousedown — start vertex drag (zone or wall)
  const handleOverlayMouseDown = (e) => {
    if (activeTool !== TOOLS.SELECT) return;
    const rect = overlayCanvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left, y = e.clientY - rect.top;

    // Opening drag on zone edge
    if (selectedZoneId) {
      const zone = zones.find(z => z.id === selectedZoneId);
      if (zone && zone.openings) {
        for (let oIdx = 0; oIdx < zone.openings.length; oIdx++) {
          const op = zone.openings[oIdx];
          const eIdx = EDGE_LABELS.indexOf(op.edgeLabel);
          if (eIdx < 0 || eIdx >= zone.points.length) continue;
          const thick = zone.edgeThickness?.[eIdx];
          if (!thick) continue;
          const ji = (eIdx + 1) % zone.points.length;
          const pa = zone.points[eIdx], pb = zone.points[ji];
          const edx = pb.x - pa.x, edy = pb.y - pa.y;
          const elen = Math.sqrt(edx * edx + edy * edy);
          if (elen === 0) continue;
          const edgeLenMm = elen / ppmBase * 1000;
          const t1 = (op.positionMm || 0) / (edgeLenMm || 1);
          const t2 = ((op.positionMm || 0) + (op.widthMm || 0)) / (edgeLenMm || 1);
          const dp1 = { x: (pa.x + edx * t1) * scale, y: (pa.y + edy * t1) * scale };
          const dp2 = { x: (pa.x + edx * t2) * scale, y: (pa.y + edy * t2) * scale };
          if (Math.sqrt((x - dp1.x) ** 2 + (y - dp1.y) ** 2) < 8) {
            setDraggingOpening({ zoneId: zone.id, openingIdx: oIdx, dragPoint: 'start' });
            e.preventDefault(); return;
          }
          if (Math.sqrt((x - dp2.x) ** 2 + (y - dp2.y) ** 2) < 8) {
            setDraggingOpening({ zoneId: zone.id, openingIdx: oIdx, dragPoint: 'end' });
            e.preventDefault(); return;
          }
        }
      }
    }

    // Wall vertex drag
    if (selectedWallId) {
      const wall = walls.find(w => w.id === selectedWallId);
      if (wall) {
        for (const pt of ['p1', 'p2']) {
          const px = wall[pt].x * scale, py = wall[pt].y * scale;
          if (Math.sqrt((x - px) ** 2 + (y - py) ** 2) < 10) {
            setDraggingWallPoint({ wallId: wall.id, point: pt });
            e.preventDefault(); return;
          }
        }
      }
    }

    if (!selectedZoneId) return;
    const zone = zones.find(z => z.id === selectedZoneId);
    if (!zone) return;
    for (let i = 0; i < zone.points.length; i++) {
      const vpx = zone.points[i].x * scale, vpy = zone.points[i].y * scale;
      if (Math.sqrt((x - vpx) ** 2 + (y - vpy) ** 2) < 10) {
        setDraggingPointIdx(i);
        setPointContextMenu(null);
        e.preventDefault();
        return;
      }
    }
  };

  // Overlay mouseup — stop drag
  const handleOverlayMouseUp = () => {
    if (draggingPointIdx !== null) { setDraggingPointIdx(null); setAlignGuides([]); }
    if (draggingWallPoint) setDraggingWallPoint(null);
    if (draggingOpening) setDraggingOpening(null);
  };

  // Right click on vertex — context menu
  const handleOverlayContextMenu = (e) => {
    e.preventDefault();
    const rect = overlayCanvasRef.current.getBoundingClientRect();
    const cx = e.clientX - rect.left, cy = e.clientY - rect.top;

    // Check if clicking near grid origin
    if (calibrated && Math.sqrt((cx - gridOrigin.x) ** 2 + (cy - gridOrigin.y) ** 2) < 15) {
      setOriginContextMenu({ x: cx, y: cy });
      return;
    }
    setOriginContextMenu(null);

    if (activeTool === TOOLS.SELECT && selectedZoneId) {
      const rect = overlayCanvasRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left, y = e.clientY - rect.top;
      const zone = zones.find(z => z.id === selectedZoneId);
      if (zone) {
        for (let i = 0; i < zone.points.length; i++) {
          const vpx = zone.points[i].x * scale, vpy = zone.points[i].y * scale;
          if (Math.sqrt((x - vpx) ** 2 + (y - vpy) ** 2) < 12) {
            setPointContextMenu({ zoneId: zone.id, pointIdx: i, x, y });
            return;
          }
        }
      }
    }
    cancelCurrentZone();
  };

  // Delete point from zone (min 3)
  const deleteZonePoint = (zoneId, pointIdx) => {
    const zone = zones.find(z => z.id === zoneId);
    if (!zone || zone.points.length <= 3) { setPointContextMenu(null); return; }
    setZones(zones.map(z => z.id === zoneId ? { ...z, points: z.points.filter((_, i) => i !== pointIdx) } : z));
    setPointContextMenu(null);
  };

  // Mouse move — snap preview + vertex dragging with alignment
  const handleOverlayMouseMove = (e) => {
    const rect = overlayCanvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left, y = e.clientY - rect.top;

    // Vertex dragging
    if (draggingPointIdx !== null && selectedZoneId) {
      const snapped = snapToGrid(x, y);
      let newPdf = { x: snapped.x / scale, y: snapped.y / scale };
      const zone = zones.find(z => z.id === selectedZoneId);
      if (zone) {
        const guides = [];
        const thresh = 5 / scale;
        zone.points.forEach((p, i) => {
          if (i === draggingPointIdx) return;
          if (Math.abs(newPdf.x - p.x) < thresh) { newPdf.x = p.x; guides.push({ axis: 'v', pos: p.x }); }
          if (Math.abs(newPdf.y - p.y) < thresh) { newPdf.y = p.y; guides.push({ axis: 'h', pos: p.y }); }
        });
        setAlignGuides(guides);
        const newPts = [...zone.points]; newPts[draggingPointIdx] = newPdf;
        setZones(zones.map(z => z.id === selectedZoneId ? { ...z, points: newPts } : z));
      }
      return;
    }

    // Opening drag along edge
    if (draggingOpening) {
      const zone = zones.find(z => z.id === draggingOpening.zoneId);
      if (zone && zone.openings) {
        const op = zone.openings[draggingOpening.openingIdx];
        if (op) {
          const eIdx = EDGE_LABELS.indexOf(op.edgeLabel);
          if (eIdx >= 0 && eIdx < zone.points.length) {
            const ji = (eIdx + 1) % zone.points.length;
            const pa = zone.points[eIdx], pb = zone.points[ji];
            const edx = pb.x - pa.x, edy = pb.y - pa.y;
            const elen = Math.sqrt(edx * edx + edy * edy);
            if (elen > 0) {
              const pdfX = x / scale, pdfY = y / scale;
              const t = Math.max(0, Math.min(1, ((pdfX - pa.x) * edx + (pdfY - pa.y) * edy) / (elen * elen)));
              const tMmRaw = t * elen / ppmBase * 1000;
              const tMm = Math.round(tMmRaw / 10) * 10; // snap to 10mm
              const newOpenings = [...zone.openings];
              if (draggingOpening.dragPoint === 'start') {
                const end = (op.positionMm || 0) + (op.widthMm || 0);
                const newPos = Math.max(0, Math.min(tMm, end - 50));
                newOpenings[draggingOpening.openingIdx] = { ...op, positionMm: newPos, widthMm: end - newPos };
              } else {
                const start = op.positionMm || 0;
                const newEnd = Math.max(start + 50, tMm);
                newOpenings[draggingOpening.openingIdx] = { ...op, widthMm: newEnd - start };
              }
              setZones(zones.map(z => z.id === zone.id ? { ...z, openings: newOpenings } : z));
            }
          }
        }
      }
      return;
    }

    // Wall vertex drag
    if (draggingWallPoint) {
      const snapped = snapToGrid(x, y);
      const pdf = { x: snapped.x / scale, y: snapped.y / scale };
      setWalls(walls.map(w => w.id !== draggingWallPoint.wallId ? w : { ...w, [draggingWallPoint.point]: pdf }));
      return;
    }

    // Zone tool: snap to grid + wall edges
    if (activeTool === TOOLS.ZONE && calibrated && gridStep > 0) {
      let snapped = snapToGrid(x, y);
      let pdfPt = { x: snapped.x / scale, y: snapped.y / scale };
      // Snap to wall edges (inner + outer)
      const wallEdges = getAllWallEdges();
      const SNAP_DIST = 50 / 1000 * ppmBase; // 50mm
      wallEdges.forEach(we => {
        // Snap to edge corners
        for (const pt of [we.p1, we.p2]) {
          const d = Math.sqrt((pdfPt.x - pt.x) ** 2 + (pdfPt.y - pt.y) ** 2);
          if (d < SNAP_DIST) { pdfPt = { ...pt }; snapped = { x: pt.x * scale, y: pt.y * scale }; }
        }
        // Snap to edge line
        const wdx = we.p2.x - we.p1.x, wdy = we.p2.y - we.p1.y;
        const wlen = Math.sqrt(wdx * wdx + wdy * wdy);
        if (wlen > 0) {
          const t = Math.max(0, Math.min(1, ((pdfPt.x - we.p1.x) * wdx + (pdfPt.y - we.p1.y) * wdy) / (wlen * wlen)));
          const projX = we.p1.x + t * wdx, projY = we.p1.y + t * wdy;
          const d = Math.sqrt((pdfPt.x - projX) ** 2 + (pdfPt.y - projY) ** 2);
          if (d < SNAP_DIST) { pdfPt = { x: projX, y: projY }; snapped = { x: projX * scale, y: projY * scale }; }
        }
      });
      setHoveredSnapPoint(snapped);
      if (rectFirstCorner) setRectLiveCorner(pdfPt);
    } else if (activeTool === TOOLS.WALL && calibrated) {
      // Wall tool: snap to grid first
      let snapped = snapToGrid(x, y);
      let pdfPt = { x: snapped.x / scale, y: snapped.y / scale };
      const guides = [];

      // Snap to all wall edges (inner + outer, from zones and standalone walls)
      const WALL_SNAP_DIST = 50 / 1000 * ppmBase;
      const wallEdges = getAllWallEdges();
      wallEdges.forEach(we => {
        for (const pt of [we.p1, we.p2]) {
          const d = Math.sqrt((pdfPt.x - pt.x) ** 2 + (pdfPt.y - pt.y) ** 2);
          if (d < WALL_SNAP_DIST) { pdfPt = { ...pt }; snapped = { x: pt.x * scale, y: pt.y * scale }; }
        }
        const wdx = we.p2.x - we.p1.x, wdy = we.p2.y - we.p1.y;
        const wlen = Math.sqrt(wdx * wdx + wdy * wdy);
        if (wlen > 0) {
          const t = Math.max(0, Math.min(1, ((pdfPt.x - we.p1.x) * wdx + (pdfPt.y - we.p1.y) * wdy) / (wlen * wlen)));
          const projX = we.p1.x + t * wdx, projY = we.p1.y + t * wdy;
          const d = Math.sqrt((pdfPt.x - projX) ** 2 + (pdfPt.y - projY) ** 2);
          if (d < WALL_SNAP_DIST) { pdfPt = { x: projX, y: projY }; snapped = { x: projX * scale, y: projY * scale }; }
        }
      });

      // Horizontal/vertical alignment with first point
      if (wallFirstPoint) {
        const ALIGN_THRESH = 5 / scale; // 5px in PDF coords
        if (Math.abs(pdfPt.x - wallFirstPoint.x) < ALIGN_THRESH) {
          pdfPt.x = wallFirstPoint.x;
          snapped.x = wallFirstPoint.x * scale;
          guides.push({ axis: 'v', pos: wallFirstPoint.x });
        }
        if (Math.abs(pdfPt.y - wallFirstPoint.y) < ALIGN_THRESH) {
          pdfPt.y = wallFirstPoint.y;
          snapped.y = wallFirstPoint.y * scale;
          guides.push({ axis: 'h', pos: wallFirstPoint.y });
        }
        setWallLivePoint(pdfPt);
      }

      setAlignGuides(guides);
      setHoveredSnapPoint(snapped);
    } else if (activeTool === TOOLS.RULER && calibrated) {
      // Ruler cursor
      const snapped = snapToGrid(x, y);
      if (rulerPoints.length > 0) {
        setRulerCursor({ x: snapped.x / scale, y: snapped.y / scale });
        setHoveredSnapPoint(snapped);
      } else {
        setHoveredSnapPoint(snapped);
      }
    } else { setHoveredSnapPoint(null); }

    // Calibration axis hint
    if (activeTool === TOOLS.CALIBRATE && calibrationPoints.length === 1) {
      const p1 = calibrationPoints[0];
      const dx = Math.abs(x - p1.x), dy = Math.abs(y - p1.y);
      const angle = Math.atan2(dy, dx) * 180 / Math.PI;
      if (angle < 15) setCalibAxisSnap('h');
      else if (angle > 75) setCalibAxisSnap('v');
      else setCalibAxisSnap(null);
    }
  };



  const isPointInPolygon = (x, y, points) => {
    let inside = false;
    for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
      const xi = points[i].x, yi = points[i].y;
      const xj = points[j].x, yj = points[j].y;
      if ((yi > y) !== (yj > y) && x < (xj - xi) * (y - yi) / (yj - yi) + xi) inside = !inside;
    }
    return inside;
  };

  // Pan — tool or middle mouse button
  const handleMouseDown = (e) => {
    if (activeTool === TOOLS.PAN || e.button === 1) {
      e.preventDefault();
      setIsPanning(true);
      setPanStart({ x: e.clientX - panOffset.x, y: e.clientY - panOffset.y });
    }
  };
  const handleMouseMove = (e) => { if (isPanning) setPanOffset({ x: e.clientX - panStart.x, y: e.clientY - panStart.y }); };
  const handleMouseUp = () => setIsPanning(false);

  // Apply calibration — input is in mm, store base values at scale=1
  const applyCalibration = () => {
    if (calibrationPoints.length !== 2 || !calibrationDistance) return;
    const dx = calibrationPoints[1].x - calibrationPoints[0].x;
    const dy = calibrationPoints[1].y - calibrationPoints[0].y;
    const pixelDist = Math.sqrt(dx * dx + dy * dy);
    const realDistMm = parseFloat(calibrationDistance);
    if (realDistMm <= 0 || isNaN(realDistMm)) return;
    const realDistM = realDistMm / 1000;

    // pixelsPerMeter at current scale
    const pxPerMAtScale = pixelDist / realDistM;
    // Base (scale=1): divide by current scale
    const base = pxPerMAtScale / scale;

    setPpmBase(base);
    // Grid origin in PDF coords (divide pixel position by scale)
    setGridOriginPdf({ x: calibrationPoints[0].x / scale, y: calibrationPoints[0].y / scale });
    setCalibrated(true);
    setShowGrid(true);
    setCalibrationPoints([]);
    setCalibrationStep(0);
    setActiveTool(TOOLS.ZONE);
  };

  // Recalibrate
  const startRecalibration = () => {
    setCalibrated(false);
    setShowGrid(false);
    setCalibrationPoints([]);
    setCalibrationStep(1);
    setActiveTool(TOOLS.CALIBRATE);
  };

  // Split zone edges where walls partially cover them — returns modified points + edge params
  const splitEdgesByWalls = (zonePoints) => {
    const wallEdges = getAllWallEdges();
    const TOLERANCE = ppmBase > 0 ? 60 / 1000 * ppmBase : 5;
    let points = [...zonePoints];
    let mats = {}, tags = {}, thicks = {};
    let modified = false;

    // Iterate edges (may grow as we insert points)
    let i = 0;
    let safety = 0;
    while (i < points.length && safety < 100) {
      safety++;
      const j = (i + 1) % points.length;
      const a = points[i], b = points[j];
      const edx = b.x - a.x, edy = b.y - a.y;
      const elen = Math.sqrt(edx * edx + edy * edy);
      if (elen === 0) { i++; continue; }

      // Find wall edges that partially overlap this zone edge
      let bestPartial = null;
      wallEdges.forEach(we => {
        const wdx = we.p2.x - we.p1.x, wdy = we.p2.y - we.p1.y;
        const wlen = Math.sqrt(wdx * wdx + wdy * wdy);
        if (wlen === 0) return;
        // Parallel check
        const cross = Math.abs((edx / elen) * (wdy / wlen) - (edy / elen) * (wdx / wlen));
        if (cross > 0.15) return;
        // Distance check
        const emx = (a.x + b.x) / 2, emy = (a.y + b.y) / 2;
        const wmx = (we.p1.x + we.p2.x) / 2, wmy = (we.p1.y + we.p2.y) / 2;
        const dist = Math.abs((emx - wmx) * (-edy / elen) + (emy - wmy) * (edx / elen));
        if (dist > TOLERANCE) return;
        // Project wall endpoints onto zone edge (0..1)
        const t1 = ((we.p1.x - a.x) * edx + (we.p1.y - a.y) * edy) / (elen * elen);
        const t2 = ((we.p2.x - a.x) * edx + (we.p2.y - a.y) * edy) / (elen * elen);
        const tStart = Math.max(0, Math.min(t1, t2));
        const tEnd = Math.min(1, Math.max(t1, t2));
        if (tEnd - tStart < 0.02) return; // too small overlap
        // Check if partial (doesn't cover full edge)
        const isPartial = tStart > 0.02 || tEnd < 0.98;
        const coversStart = tStart <= 0.02;
        const coversEnd = tEnd >= 0.98;
        if (!we.material && !we.tag) return; // no useful data
        if (!bestPartial || (isPartial && !bestPartial.isPartial)) {
          bestPartial = { ...we, tStart, tEnd, isPartial, coversStart, coversEnd };
        }
      });

      if (bestPartial && bestPartial.isPartial) {
        // Need to split — insert point where wall ends
        const splitT = bestPartial.coversStart ? bestPartial.tEnd : bestPartial.tStart;
        const splitPoint = { x: a.x + edx * splitT, y: a.y + edy * splitT };
        // Insert point
        points.splice(j, 0, splitPoint);
        // The covered part gets wall params
        const coveredIdx = bestPartial.coversStart ? i : i + 1;
        if (bestPartial.material) mats[coveredIdx] = bestPartial.material;
        if (bestPartial.tag) tags[coveredIdx] = bestPartial.tag;
        if (bestPartial.thickness) thicks[coveredIdx] = bestPartial.thickness;
        modified = true;
        // Don't increment i — recheck this edge (it was split)
        continue;
      } else if (bestPartial && !bestPartial.isPartial) {
        // Full coverage — just copy params
        if (bestPartial.material) mats[i] = bestPartial.material;
        if (bestPartial.tag) tags[i] = bestPartial.tag;
        if (bestPartial.thickness) thicks[i] = bestPartial.thickness;
      }
      i++;
    }

    return { points, edgeMaterials: mats, edgeTags: tags, edgeThickness: thicks, modified };
  };

  // Find shared edges — check if new zone edges lie on any existing wall edge (inner or outer)
  const findSharedEdges = (newPoints) => {
    const shared = [];
    const wallEdges = getAllWallEdges();
    const TOLERANCE = ppmBase > 0 ? 60 / 1000 * ppmBase : 5; // 60mm

    newPoints.forEach((np, nIdx) => {
      const nj = (nIdx + 1) % newPoints.length;
      const na = newPoints[nIdx], nb = newPoints[nj];
      const nmx = (na.x + nb.x) / 2, nmy = (na.y + nb.y) / 2;
      const ndx = nb.x - na.x, ndy = nb.y - na.y;
      const nlen = Math.sqrt(ndx * ndx + ndy * ndy);
      if (nlen === 0) return;

      let bestMatch = null;
      wallEdges.forEach(we => {
        const edx = we.p2.x - we.p1.x, edy = we.p2.y - we.p1.y;
        const elen = Math.sqrt(edx * edx + edy * edy);
        if (elen === 0) return;

        // Parallel check
        const cross = Math.abs((ndx / nlen) * (edy / elen) - (ndy / nlen) * (edx / elen));
        if (cross > 0.15) return;

        // Distance between midpoints projected onto normal
        const emx = (we.p1.x + we.p2.x) / 2, emy = (we.p1.y + we.p2.y) / 2;
        const dist = Math.abs((nmx - emx) * (-ndy / nlen) + (nmy - emy) * (ndx / nlen));
        if (dist > TOLERANCE) return;

        // Overlap check
        const t1 = ((na.x - we.p1.x) * edx + (na.y - we.p1.y) * edy) / (elen * elen);
        const t2 = ((nb.x - we.p1.x) * edx + (nb.y - we.p1.y) * edy) / (elen * elen);
        const tMin = Math.max(0, Math.min(t1, t2));
        const tMax = Math.min(1, Math.max(t1, t2));
        if (tMax - tMin < 0.1) return;

        if (!bestMatch || dist < bestMatch.dist) {
          bestMatch = { ...we, dist };
        }
      });

      if (bestMatch && bestMatch.material) {
        const srcZone = bestMatch.zoneId ? zones.find(z => z.id === bestMatch.zoneId) : null;
        shared.push({
          newEdgeIdx: nIdx,
          existingZoneId: bestMatch.zoneId || null,
          existingZoneName: srcZone?.name || 'Стіна',
          existingEdgeIdx: bestMatch.edgeIdx ?? -1,
          existingEdgeLabel: bestMatch.edgeIdx >= 0 ? (EDGE_LABELS[bestMatch.edgeIdx] || '?') : '?',
          material: bestMatch.material || '',
          thickness: bestMatch.thickness || 0,
          tag: bestMatch.tag || '',
          edgeType: bestMatch.type,
        });
      }
    });
    return shared;
  };

  // Shared edge confirmation state
  const [sharedEdgePrompts, setSharedEdgePrompts] = useState([]); // [{newEdgeIdx, ...data}]
  const [pendingNewZone, setPendingNewZone] = useState(null); // zone waiting for confirmation

  const cancelCurrentZone = () => { setRectFirstCorner(null); setRectLiveCorner(null); };
  const deleteZone = (id) => { setZones(zones.filter(z => z.id !== id)); if (selectedZoneId === id) setSelectedZoneId(null); };
  const renameZone = (id, name) => { setZones(zones.map(z => z.id === id ? { ...z, name } : z)); setZoneNameEdit(null); };


  // Keyboard
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') {
        if (activeTool === TOOLS.RULER && rulerPoints.length > 0) { setShowRulerSaveDialog(true); return; }
        cancelCurrentZone(); setPointContextMenu(null); setOriginContextMenu(null);
        setWallFirstPoint(null); setWallLivePoint(null);
        setSelectedZoneId(null); setSelectedWallId(null); setHighlightedEdge(null);
        if (calibrationStep > 0 && !calibrated) { setCalibrationPoints([]); setCalibrationStep(1); }
        else if (calibrated) setActiveTool(TOOLS.SELECT);
      }
      if (e.key === 'Delete' && calibrated) {
        if (selectedWallId) { setWalls(prev => prev.filter(w => w.id !== selectedWallId)); setSelectedWallId(null); }
        else if (selectedZoneId) deleteZone(selectedZoneId);
      }
      if (calibrated) {
        if (e.key === 'z' || e.key === 'я') setActiveTool(TOOLS.ZONE);
        if (e.key === 'v' || e.key === 'м') setActiveTool(TOOLS.SELECT);
        if (e.key === 'g' || e.key === 'п') setShowGrid(g => !g);
        if (e.key === 'l' || e.key === 'д') { setActiveTool(TOOLS.RULER); setRulerPoints([]); setRulerCursor(null); }
        if (e.key === 'w' || e.key === 'ц') { setActiveTool(TOOLS.WALL); setWallFirstPoint(null); setWallLivePoint(null); }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [calibrated, calibrationStep, activeTool, rulerPoints, selectedZoneId]);

  // File drop
  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && file.type === 'application/pdf') loadPDF(file);
  };

  return (
    <div className="fixed inset-0 bg-gray-100 z-50 flex flex-col" style={{ fontFamily: 'Roboto, sans-serif' }} onDrop={handleDrop} onDragOver={(e) => e.preventDefault()}>
      {/* Top bar */}
      <div className="bg-white border-b shadow-sm px-4 py-2 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <span className="font-bold text-lg" style={{ color: BRAND.navy, fontFamily: 'Archivo Black, sans-serif' }}>LIHTAR</span>
          <span className="text-sm font-medium" style={{ color: BRAND.orange }}>Drawing Reader</span>
          <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">MVP</span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => fileInputRef.current?.click()} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-white rounded transition-colors" style={{ backgroundColor: BRAND.navy }}>
            <Upload className="w-4 h-4" /> Завантажити PDF
          </button>
          <input ref={fileInputRef} type="file" accept=".pdf" className="hidden" onChange={(e) => { if (e.target.files[0]) loadPDF(e.target.files[0]); }} />

          <div className="w-px h-6 bg-gray-200 mx-2"></div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded"><X className="w-5 h-5 text-gray-600" /></button>
        </div>
      </div>

      {/* Tool bar — only after calibration */}
      {pdfDoc && calibrated && (
        <div className="bg-white border-b px-4 py-1.5 flex items-center gap-1 flex-shrink-0">
          <ToolBtn icon={MousePointer2} label="Вибір (V)" active={activeTool === TOOLS.SELECT} onClick={() => setActiveTool(TOOLS.SELECT)} />
          <ToolBtn icon={Move} label="Переміщення" active={activeTool === TOOLS.PAN} onClick={() => setActiveTool(TOOLS.PAN)} />
          <ToolBtn icon={Pencil} label="Зона (Z)" active={activeTool === TOOLS.ZONE} onClick={() => setActiveTool(TOOLS.ZONE)} />
          <ToolBtn icon={Minus} label="Стіна (W)" active={activeTool === TOOLS.WALL} onClick={() => { setActiveTool(TOOLS.WALL); setWallFirstPoint(null); setWallLivePoint(null); }} />
          <ToolBtn icon={Ruler} label="Лінійка (L)" active={activeTool === TOOLS.RULER} onClick={() => { setActiveTool(TOOLS.RULER); setRulerPoints([]); setRulerCursor(null); }} />

          <div className="w-px h-6 bg-gray-200 mx-2"></div>

          <button onClick={() => setShowGrid(!showGrid)} className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded transition-colors ${showGrid ? 'text-white' : 'text-gray-600 hover:bg-gray-100'}`} style={showGrid ? { backgroundColor: BRAND.navy } : {}}>
            {showGrid ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />} Сітка (G)
          </button>

          <button onClick={startRecalibration} className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-gray-500 hover:bg-gray-100 rounded ml-1">
            <Ruler className="w-3.5 h-3.5" /> Перекалібрувати
          </button>


          <div className="ml-auto flex items-center gap-2 text-xs">
            <span className="text-green-600 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Калібровано: {pixelsPerMeter.toFixed(1)} px/м | Snap: 1 см</span>
            {rectFirstCorner && (
              <>
                <span className="text-gray-400">|</span>
                <span style={{ color: BRAND.orange }}>Клікніть на протилежний кут прямокутника</span>
                <button onClick={cancelCurrentZone} className="text-red-500 hover:text-red-700 ml-1">Скасувати</button>
              </>
            )}
            {selectedZoneId && activeTool === TOOLS.SELECT && !rectFirstCorner && (
              <>
                <span className="text-gray-400">|</span>
                <span className="text-gray-500">Перетягніть вершину | Клікніть на ребро = нова точка | ПКМ = меню</span>
              </>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-1 overflow-hidden">
        {/* Canvas */}
        <div
          ref={containerRef}
          className="flex-1 overflow-auto bg-gray-200 relative"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          style={{ cursor: activeTool === TOOLS.PAN ? (isPanning ? 'grabbing' : 'grab') : activeTool === TOOLS.ZONE ? 'crosshair' : activeTool === TOOLS.WALL ? 'crosshair' : activeTool === TOOLS.RULER ? 'crosshair' : activeTool === TOOLS.CALIBRATE ? 'crosshair' : 'default' }}
        >
          {!pdfDoc ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center p-12 border-2 border-dashed border-gray-300 rounded-2xl bg-white cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors" onClick={() => fileInputRef.current?.click()}>
                <Upload className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-lg font-medium" style={{ color: BRAND.navy }}>Завантажте PDF дизайн-проєкту</p>
                <p className="text-sm text-gray-400 mt-1">Перетягніть файл сюди або натисніть</p>
              </div>
            </div>
          ) : (
            <div className="inline-block p-4" style={{ transform: `translate(${panOffset.x}px, ${panOffset.y}px)` }}>
              <div className="relative shadow-lg">
                <canvas ref={canvasRef} className="block" />
                <canvas
                  ref={overlayCanvasRef}
                  className="absolute top-0 left-0"
                  onClick={handleCanvasClick}
                  onMouseDown={handleOverlayMouseDown}
                  onMouseMove={handleOverlayMouseMove}
                  onMouseUp={handleOverlayMouseUp}
                  onContextMenu={handleOverlayContextMenu}
                  style={{ cursor: draggingPointIdx !== null ? 'grabbing' : undefined }}
                />
              </div>
            </div>
          )}

          {/* Point context menu — stays relative to canvas */}
          {pointContextMenu && (
            <div className="absolute bg-white rounded-lg shadow-xl border py-1 z-30" style={{ left: pointContextMenu.x + 4, top: pointContextMenu.y + 4, borderColor: BRAND.navy }}>
              <button className="flex items-center gap-2 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-100 w-full text-left"
                onClick={() => { setDraggingPointIdx(pointContextMenu.pointIdx); setPointContextMenu(null); }}>
                <Move className="w-3 h-3" /> Перемістити
              </button>
              {zones.find(z => z.id === pointContextMenu.zoneId)?.points.length > 3 && (
                <button className="flex items-center gap-2 px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 w-full text-left"
                  onClick={() => deleteZonePoint(pointContextMenu.zoneId, pointContextMenu.pointIdx)}>
                  <Trash2 className="w-3 h-3" /> Видалити точку
                </button>
              )}
            </div>
          )}
        </div>

        {/* Bottom toolbar — zoom + pages */}
        {pdfDoc && (
          <div className="fixed bottom-4 left-1/2 -translate-x-1/2 bg-white rounded-xl shadow-lg border px-3 py-1.5 flex items-center gap-2 z-30" style={{ borderColor: BRAND.navy + '30' }}>
            {/* Page navigation */}
            <button onClick={() => setPageNum(1)} disabled={pageNum <= 1} className="p-1 rounded hover:bg-gray-100 disabled:opacity-20" title="Перша сторінка">
              <ChevronsLeft className="w-4 h-4" style={{ color: BRAND.navy }} />
            </button>
            <button onClick={() => setPageNum(Math.max(1, pageNum - 1))} disabled={pageNum <= 1} className="p-1 rounded hover:bg-gray-100 disabled:opacity-20" title="Попередня">
              <ChevronLeft className="w-4 h-4" style={{ color: BRAND.navy }} />
            </button>
            <span className="text-xs font-medium px-1" style={{ color: BRAND.navy }}>{pageNum} / {totalPages}</span>
            <button onClick={() => setPageNum(Math.min(totalPages, pageNum + 1))} disabled={pageNum >= totalPages} className="p-1 rounded hover:bg-gray-100 disabled:opacity-20" title="Наступна">
              <ChevronRight className="w-4 h-4" style={{ color: BRAND.navy }} />
            </button>
            <button onClick={() => setPageNum(totalPages)} disabled={pageNum >= totalPages} className="p-1 rounded hover:bg-gray-100 disabled:opacity-20" title="Остання сторінка">
              <ChevronsRight className="w-4 h-4" style={{ color: BRAND.navy }} />
            </button>

            <div className="w-px h-5 bg-gray-200 mx-1"></div>

            {/* Zoom */}
            <button onClick={() => setScale(s => Math.max(0.25, Math.round((s - 0.25) * 100) / 100))} className="p-1 rounded hover:bg-gray-100" title="Зменшити">
              <Minus className="w-4 h-4" style={{ color: BRAND.navy }} />
            </button>
            <input
              type="range"
              min="25" max="400" step="25"
              value={Math.round(scale * 100)}
              onChange={(e) => setScale(Number(e.target.value) / 100)}
              className="w-28 cursor-pointer"
              style={{ accentColor: BRAND.orange }}
              title={`${Math.round(scale * 100)}%`}
            />
            <button onClick={() => setScale(s => Math.min(4, Math.round((s + 0.25) * 100) / 100))} className="p-1 rounded hover:bg-gray-100" title="Збільшити">
              <Plus className="w-4 h-4" style={{ color: BRAND.navy }} />
            </button>

            {/* Scale display + presets */}
            <select
              value=""
              onChange={(e) => { if (e.target.value) setScale(Number(e.target.value) / 100); }}
              className="text-xs border rounded px-1.5 py-1 cursor-pointer"
              style={{ color: BRAND.navy, borderColor: BRAND.navy + '30', minWidth: 60 }}
            >
              <option value="" disabled>{Math.round(scale * 100)}%</option>
              {[25, 50, 75, 100, 125, 150, 200, 300, 400].map(v => (
                <option key={v} value={v}>{v}%</option>
              ))}
            </select>
          </div>
        )}

        {/* === UI Panels — fixed to interface, not to canvas === */}


        {/* Calibration guide — top left */}
        {pdfDoc && !calibrated && (
          <div className="fixed top-28 left-4 bg-white rounded-xl shadow-lg p-5 max-w-sm z-30 border" style={{ borderColor: BRAND.orange }}>
            <div className="flex items-center gap-2 mb-3">
              <Target className="w-5 h-5" style={{ color: BRAND.orange }} />
              <h3 className="font-bold text-sm" style={{ color: BRAND.navy }}>Калібрування сітки</h3>
            </div>
            {calibrationStep === 1 && (
              <div className="space-y-2 text-xs text-gray-600">
                <p><strong style={{ color: BRAND.navy }}>1.</strong> Оберіть <strong>найбільший відомий розмір</strong> на кресленні</p>
                <p><strong style={{ color: BRAND.navy }}>2.</strong> Клікніть на <strong>початок</strong> лінії — це буде точка прив'язки сітки</p>
                <div className="rounded-lg p-2 mt-1" style={{ backgroundColor: BRAND.orange + '10' }}>
                  <p style={{ color: BRAND.orange }}><AlertCircle className="w-3 h-3 inline mr-1" />Будьте точним. Крок сітки: 1 см.</p>
                </div>
              </div>
            )}
            {calibrationStep === 2 && (
              <div className="space-y-2 text-xs">
                <p style={{ color: BRAND.orange }}><CheckCircle2 className="w-3.5 h-3.5 inline mr-1" />Перша точка встановлена</p>
                <p className="text-gray-600">Клікніть на <strong>кінець</strong> лінії розміру</p>
              </div>
            )}
            {calibrationStep === 3 && (
              <div className="space-y-3">
                <p className="text-xs" style={{ color: BRAND.orange }}><CheckCircle2 className="w-3.5 h-3.5 inline mr-1" />Дві точки встановлено</p>
                <p className="text-xs text-gray-600">Відстань між точками (мм):</p>
                <div className="flex items-center gap-2">
                  <input type="number" step="1" placeholder="4800" value={calibrationDistance}
                    onChange={(e) => setCalibrationDistance(e.target.value)} autoFocus
                    className="flex-1 border-2 rounded-lg px-3 py-2 text-lg focus:outline-none"
                    style={{ borderColor: BRAND.orange }}
                    onKeyDown={(e) => { if (e.key === 'Enter') applyCalibration(); }} />
                  <span className="text-gray-500 text-lg">мм</span>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => { setCalibrationPoints([]); setCalibrationStep(1); }} className="flex-1 px-3 py-1.5 border rounded-lg text-gray-600 text-xs">Заново</button>
                  <button onClick={applyCalibration} className="flex-1 px-3 py-1.5 text-white rounded-lg text-xs font-medium"
                    style={{ backgroundColor: calibrationDistance && parseFloat(calibrationDistance) > 0 ? BRAND.orange : '#d1d5db' }}
                    disabled={!calibrationDistance || parseFloat(calibrationDistance) <= 0}>Калібрувати</button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Ruler info — top left */}
        {activeTool === TOOLS.RULER && rulerPoints.length > 0 && (
          <div className="fixed top-28 left-4 bg-white rounded-xl shadow-lg p-4 z-30 border" style={{ borderColor: BRAND.navy, minWidth: 200 }}>
            <div className="text-xs font-semibold mb-2" style={{ color: BRAND.navy }}>Лінійка</div>
            {rulerPoints.length >= 2 && rulerPoints.slice(0, -1).map((p, i) => (
              <div key={i} className="flex justify-between text-xs text-gray-600 mb-1">
                <span>Відстань {i + 1}→{i + 2}:</span>
                <span className="font-semibold" style={{ color: BRAND.navy }}>{pdfDist(rulerPoints[i], rulerPoints[i + 1]).toFixed(2)} м</span>
              </div>
            ))}
            {rulerCursor && (
              <div className="flex justify-between text-xs mb-1">
                <span style={{ color: BRAND.orange }}>Відстань:</span>
                <span className="font-bold" style={{ color: BRAND.orange }}>{pdfDist(rulerPoints[rulerPoints.length - 1], rulerCursor).toFixed(2)} м</span>
              </div>
            )}
            {rulerPoints.length >= 2 && rulerCursor && (() => {
              const area = calcArea([...rulerPoints, rulerCursor]);
              return area > 0.01 ? (
                <div className="flex justify-between text-xs pt-1 border-t mt-1">
                  <span style={{ color: BRAND.navy }}>Площа:</span>
                  <span className="font-bold" style={{ color: BRAND.navy }}>{area.toFixed(2)} м²</span>
                </div>
              ) : null;
            })()}
            <div className="text-[10px] text-gray-400 mt-2">Клік = точка | Esc = записати</div>
          </div>
        )}

        {/* Ruler save dialog — centered overlay */}
        {showRulerSaveDialog && (
          <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-lg p-5 max-w-xs border" style={{ borderColor: BRAND.orange }}>
              <h3 className="font-bold text-sm mb-3" style={{ color: BRAND.navy }}>Записати виміри?</h3>
              <div className="text-xs text-gray-600 space-y-1 mb-4">
                {rulerPoints.length === 2 && <p>Відстань: <strong>{pdfDist(rulerPoints[0], rulerPoints[1]).toFixed(2)} м</strong></p>}
                {rulerPoints.length > 2 && (
                  <><p>Периметр: <strong>{calcPerimeter(rulerPoints).toFixed(2)} м</strong></p>
                  <p>Площа: <strong>{calcArea(rulerPoints).toFixed(2)} м²</strong></p></>
                )}
              </div>
              <div className="flex gap-2">
                <button className="flex-1 px-3 py-1.5 border rounded-lg text-xs text-gray-600 hover:bg-gray-50"
                  onClick={() => { setShowRulerSaveDialog(false); setRulerPoints([]); setRulerCursor(null); }}>Скасувати</button>
                <button className="flex-1 px-3 py-1.5 text-white rounded-lg text-xs font-medium"
                  style={{ backgroundColor: BRAND.orange }}
                  onClick={() => {
                    const note = rulerPoints.length === 2
                      ? { type: 'line', name: '', value: pdfDist(rulerPoints[0], rulerPoints[1]).toFixed(2), perimeter: null, area: null }
                      : { type: 'area', name: '', value: null, perimeter: calcPerimeter(rulerPoints).toFixed(2), area: calcArea(rulerPoints).toFixed(2) };
                    setRulerNotes([...rulerNotes, { id: Date.now(), ...note }]);
                    setShowRulerSaveDialog(false); setRulerPoints([]); setRulerCursor(null);
                  }}>Записати</button>
              </div>
            </div>
          </div>
        )}


        {/* Origin context menu */}
        {originContextMenu && (
          <div className="fixed bg-white rounded-lg shadow-xl border py-1 z-40" style={{ left: originContextMenu.x + 60, top: originContextMenu.y + 120, borderColor: BRAND.navy }}>
            <button className="flex items-center gap-2 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-100 w-full text-left"
              onClick={() => { setMovingOrigin(true); setOriginContextMenu(null); }}>
              <Move className="w-3 h-3" /> Перемістити початок
            </button>
            <button className="flex items-center gap-2 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-100 w-full text-left"
              onClick={() => setOriginContextMenu(null)}>
              <Target className="w-3 h-3" /> Зафіксувати
            </button>
          </div>
        )}

        {/* Shared edge confirmation */}
        {pendingNewZone && sharedEdgePrompts.length > 0 && (
          <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-lg p-6 border" style={{ borderColor: BRAND.navy, minWidth: 340, maxWidth: 400 }}>
              <h3 className="font-bold text-base mb-3" style={{ color: BRAND.navy }}>Спільні стіни</h3>
              <p className="text-xs text-gray-500 mb-3">Знайдено ребра які лежать на стінах сусідніх кімнат:</p>
              <div className="space-y-2 mb-4">
                {sharedEdgePrompts.map((s, idx) => {
                  const tag = WALL_TAGS.find(t => t.id === s.tag);
                  return (
                    <div key={idx} className="flex items-center gap-2 p-2 rounded" style={{ backgroundColor: BRAND.navy + '08' }}>
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: tag?.color || '#9ca3af' }} />
                      <div className="flex-1 text-xs">
                        <span className="font-medium" style={{ color: BRAND.navy }}>Ребро {EDGE_LABELS[s.newEdgeIdx]}</span>
                        <span className="text-gray-400"> → {s.existingZoneName} (ребро {s.existingEdgeLabel})</span>
                        <div className="text-gray-500 mt-0.5">
                          {s.material && <span>{s.material}</span>}
                          {s.thickness > 0 && <span className="ml-1">{s.thickness}мм</span>}
                          {tag && <span className="ml-1" style={{ color: tag.color }}>{tag.label}</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex gap-2">
                <button className="flex-1 px-4 py-2 border-2 rounded-lg text-sm text-gray-600 hover:bg-gray-50"
                  onClick={() => {
                    // Add zone without shared params
                    const z = { ...pendingNewZone, edgeMaterials: {}, edgeTags: {}, edgeThickness: {} };
                    setZones([...zones, z]);
                    setSelectedZoneId(z.id);
                    setPendingNewZone(null); setSharedEdgePrompts([]);
                  }}>Ні, вручну</button>
                <button className="flex-1 px-4 py-2 text-white rounded-lg text-sm font-medium"
                  style={{ backgroundColor: BRAND.navy }}
                  onClick={() => {
                    // Mark shared edges and copy openings
                    const nz = { ...pendingNewZone, edgeShared: {}, openings: [...(pendingNewZone.openings || [])] };
                    sharedEdgePrompts.forEach(s => {
                      if (s.existingZoneId && s.newEdgeIdx !== undefined) {
                        // Mark this edge as shared
                        nz.edgeShared = { ...nz.edgeShared, [s.newEdgeIdx]: { zoneId: s.existingZoneId, edgeIdx: s.existingEdgeIdx } };
                        // Copy openings from source zone
                        const srcZone = zones.find(z => z.id === s.existingZoneId);
                        if (srcZone?.openings) {
                          const srcLabel = EDGE_LABELS[s.existingEdgeIdx] || '';
                          const newLabel = EDGE_LABELS[s.newEdgeIdx] || '';
                          srcZone.openings.filter(o => o.edgeLabel === srcLabel).forEach(o => {
                            nz.openings.push({ ...o, edgeLabel: newLabel });
                          });
                        }
                      }
                    });
                    setZones([...zones, nz]);
                    setSelectedZoneId(nz.id);
                    setPendingNewZone(null); setSharedEdgePrompts([]);
                  }}>Підтвердити</button>
              </div>
            </div>
          </div>
        )}

        {/* Opening form popup — universal for zones and walls */}
        {showOpeningForm && (
          <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-50" onClick={() => setShowOpeningForm(null)}>
            <div className="bg-white rounded-xl shadow-lg p-6 border" style={{ borderColor: BRAND.navy, minWidth: 300 }} onClick={(e) => e.stopPropagation()}>
              <h3 className="font-bold text-base mb-4" style={{ color: BRAND.navy }}>Додати отвір</h3>
              <div className="space-y-3">
                <div>
                  <span className="text-xs text-gray-500 block mb-1">Тип</span>
                  <div className="flex gap-1">
                    {OPENING_TYPES.map(t => (
                      <button key={t.id}
                        className={`flex-1 px-2 py-2 text-xs rounded border-2 font-medium transition-colors ${(showOpeningForm.type || 'door') === t.id ? 'text-white' : 'text-gray-500 hover:bg-gray-50'}`}
                        style={(showOpeningForm.type || 'door') === t.id ? { backgroundColor: BRAND.navy, borderColor: BRAND.navy } : {}}
                        onClick={() => setShowOpeningForm({ ...showOpeningForm, type: t.id, heightMm: t.defaultHeight })}
                      >{t.label}</button>
                    ))}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">Ширина</span>
                  <div className="flex items-center gap-1">
                    <input type="number" value={showOpeningForm.widthMm || 900}
                      onChange={(e) => setShowOpeningForm({ ...showOpeningForm, widthMm: parseInt(e.target.value) || 0 })}
                      className="w-24 border-2 rounded-lg px-3 py-1.5 text-sm text-right" style={{ borderColor: BRAND.navy + '30' }} />
                    <span className="text-gray-400 text-sm">мм</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">Висота</span>
                  <div className="flex items-center gap-1">
                    <input type="number" value={showOpeningForm.heightMm || 2100}
                      onChange={(e) => setShowOpeningForm({ ...showOpeningForm, heightMm: parseInt(e.target.value) || 0 })}
                      className="w-24 border-2 rounded-lg px-3 py-1.5 text-sm text-right" style={{ borderColor: BRAND.navy + '30' }} />
                    <span className="text-gray-400 text-sm">мм</span>
                  </div>
                </div>
                <div className="flex gap-2 pt-2">
                  <button className="flex-1 px-4 py-2 border-2 rounded-lg text-sm text-gray-600 hover:bg-gray-50"
                    onClick={() => setShowOpeningForm(null)}>Скасувати</button>
                  <button className="flex-1 px-4 py-2 text-white rounded-lg text-sm font-medium"
                    style={{ backgroundColor: BRAND.navy }}
                    onClick={() => {
                      const { target, id, edgeLabel, type, widthMm, heightMm } = showOpeningForm;
                      // Calculate default position = center of edge
                      let edgeLenMm = 0;
                      if (target === 'zone') {
                        const z = zones.find(zz => zz.id === id);
                        if (z) {
                          const eIdx = EDGE_LABELS.indexOf(edgeLabel);
                          if (eIdx >= 0 && eIdx < z.points.length) {
                            const j = (eIdx + 1) % z.points.length;
                            edgeLenMm = ppmBase > 0 ? Math.round(Math.sqrt((z.points[j].x - z.points[eIdx].x) ** 2 + (z.points[j].y - z.points[eIdx].y) ** 2) / ppmBase * 1000) : 0;
                          }
                        }
                      }
                      const defaultPos = Math.max(0, Math.round((edgeLenMm - (widthMm || 900)) / 2));
                      const op = { edgeLabel, type: type || 'door', widthMm: widthMm || 900, heightMm: heightMm || 2100, positionMm: defaultPos };
                      if (target === 'zone') {
                        setZones(zones.map(z => z.id !== id ? z : { ...z, openings: [...(z.openings || []), op] }));
                      } else if (target === 'wall') {
                        setWalls(walls.map(w => w.id !== id ? w : { ...w, openings: [...(w.openings || []), op] }));
                      }
                      setShowOpeningForm(null);
                    }}>Додати</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Right panel */}
        {pdfDoc && calibrated && (
          <div className="w-72 bg-white border-l flex flex-col flex-shrink-0">
            <div className="px-4 py-3 border-b" style={{ backgroundColor: BRAND.navy }}>
              <h3 className="font-semibold text-sm text-white">Зони кімнат</h3>
              <p className="text-xs text-white/50 mt-0.5">{zones.length} {zones.length === 1 ? 'зона' : zones.length < 5 ? 'зони' : 'зон'}</p>
            </div>
            <div className="flex-1 overflow-y-auto">
              {zones.length === 0 ? (
                <div className="p-4 text-xs text-gray-400 text-center">
                  <Pencil className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                  <p>Оберіть інструмент <strong>"Зона"</strong> щоб позначити кімнати</p>
                </div>
              ) : (
                zones.map((zone, idx) => {
                  const color = ZONE_COLORS[idx % ZONE_COLORS.length];
                  const hasPoints = zone.points && zone.points.length >= 3;
                  const area = hasPoints ? calcArea(zone.points) : 0;
                  const perimeter = hasPoints ? calcPerimeter(zone.points) : 0;
                  const isActive = false;
                  const isSelected = selectedZoneId === zone.id;
                  return (
                    <div
                      key={zone.id}
                      className={`px-3 py-2.5 border-b cursor-pointer transition-colors ${isSelected ? 'bg-blue-50' : isActive ? 'bg-orange-50' : 'hover:bg-gray-50'}`}
                      style={isActive ? { borderLeft: `3px solid ${BRAND.orange}` } : {}}
                      onClick={() => { setSelectedZoneId(zone.id); setHighlightedEdge(null); }}
                    >
                      {/* Header */}
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded" style={{ backgroundColor: hasPoints ? color : '#d1d5db' }}></div>
                          {zoneNameEdit === zone.id ? (
                            <input type="text" defaultValue={zone.name} autoFocus
                              className="text-xs font-medium border rounded px-1 py-0.5 w-28"
                              onBlur={(e) => renameZone(zone.id, e.target.value)}
                              onKeyDown={(e) => { if (e.key === 'Enter') renameZone(zone.id, e.target.value); }} />
                          ) : (
                            <span className="text-xs font-semibold cursor-text" style={{ color: BRAND.navy }}
                              onClick={(e) => { e.stopPropagation(); setZoneNameEdit(zone.id); }}>{zone.name}</span>
                          )}
                        </div>
                        <button onClick={(e) => { e.stopPropagation(); deleteZone(zone.id); }} className="p-0.5 text-gray-300 hover:text-red-500">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>

                      {/* Not mapped yet */}
                      {!hasPoints && (
                        <div className="text-[11px] py-2 text-center rounded" style={{ color: BRAND.orange, backgroundColor: BRAND.orange + '10' }}>
                          {isActive ? 'Намалюйте прямокутник на плані' : 'Контури не задано'}
                        </div>
                      )}

                      {/* Mapped — show data */}
                      {hasPoints && (
                        <>
                          {/* Height */}
                          <div className="flex items-center gap-1.5 text-[11px] mb-1.5">
                            <span className="text-gray-400">Висота:</span>
                            <input type="number" value={zone.height || ''} placeholder="мм"
                              className="w-16 border rounded px-1 py-0.5 text-[11px] text-center"
                              style={{ color: BRAND.navy }}
                              onChange={(e) => setZones(zones.map(z => z.id === zone.id ? { ...z, height: parseInt(e.target.value) || 0 } : z))}
                              onClick={(e) => e.stopPropagation()} />
                            <span className="text-gray-300">мм</span>
                          </div>

                          {/* Area + Perimeter */}
                          <div className="grid grid-cols-2 gap-1.5 text-[11px] mb-1.5">
                            <div className="bg-gray-50 rounded px-2 py-1">
                              <span className="text-gray-400 block">Площа</span>
                              <span className="font-bold" style={{ color: BRAND.navy }}>{area.toFixed(2)} м²</span>
                            </div>
                            <div className="bg-gray-50 rounded px-2 py-1">
                              <span className="text-gray-400 block">Периметр</span>
                              <span className="font-bold" style={{ color: BRAND.navy }}>{perimeter.toFixed(2)} м</span>
                            </div>
                          </div>

                          {/* Edges — only when selected */}
                          {isSelected && (
                            <div className="space-y-1 mt-1.5 pt-1.5 border-t">
                              {getZoneEdges(zone.points, zone.height, zone.openings).map((edge, eIdx) => (
                                <div key={eIdx}
                                  className={`rounded px-2 py-1.5 text-[11px] cursor-pointer transition-colors ${highlightedEdge?.zoneId === zone.id && highlightedEdge?.edgeIdx === eIdx ? 'bg-orange-50 ring-1 ring-orange-300' : 'hover:bg-gray-50'}`}
                                  onClick={(e) => { e.stopPropagation(); setHighlightedEdge({ zoneId: zone.id, edgeIdx: eIdx }); }}>
                                  <div className="flex items-center justify-between mb-0.5">
                                    <span className="font-semibold" style={{ color: BRAND.navy }}>Ребро {edge.label}</span>
                                    <div className="flex items-center gap-0.5">
                                      <input type="number" value={edge.lengthMm}
                                        className="w-14 border rounded px-1 py-0.5 text-[11px] text-right"
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={(e) => {
                                          const newMm = parseInt(e.target.value) || 0;
                                          const newM = newMm / 1000;
                                          const j = (eIdx + 1) % zone.points.length;
                                          const p1 = zone.points[eIdx], p2 = zone.points[j];
                                          const cx = (p1.x + p2.x) / 2, cy = (p1.y + p2.y) / 2;
                                          const dx = p2.x - p1.x, dy = p2.y - p1.y;
                                          const curLen = Math.sqrt(dx * dx + dy * dy);
                                          if (curLen === 0) return;
                                          const halfNew = (newM * ppmBase) / 2;
                                          const ux = dx / curLen, uy = dy / curLen;
                                          const np1 = { x: cx - ux * halfNew, y: cy - uy * halfNew };
                                          const np2 = { x: cx + ux * halfNew, y: cy + uy * halfNew };
                                          const newPts = [...zone.points]; newPts[eIdx] = np1; newPts[j] = np2;
                                          setZones(zones.map(z => z.id === zone.id ? { ...z, points: newPts } : z));
                                        }} />
                                      <span className="text-gray-300">мм</span>
                                    </div>
                                  </div>
                                  {/* Material + Tag + Thickness */}
                                  <div className="space-y-1 mt-1">
                                    <div className="flex items-center gap-1">
                                      <select value={zone.edgeMaterials?.[eIdx] || ''} onClick={(e) => e.stopPropagation()}
                                        onChange={(e) => { e.stopPropagation(); setZones(zones.map(z => z.id === zone.id ? { ...z, edgeMaterials: { ...(z.edgeMaterials || {}), [eIdx]: e.target.value } } : z)); }}
                                        className="text-[10px] border rounded px-1 py-0.5 flex-1">
                                        <option value="">— матеріал —</option>
                                        {WALL_MATERIALS.map(m => <option key={m} value={m}>{m}</option>)}
                                      </select>
                                      <select value={zone.edgeTags?.[eIdx] || ''} onClick={(e) => e.stopPropagation()}
                                        onChange={(e) => { e.stopPropagation(); setZones(zones.map(z => z.id === zone.id ? { ...z, edgeTags: { ...(z.edgeTags || {}), [eIdx]: e.target.value } } : z)); }}
                                        className="text-[10px] border rounded px-1 py-0.5">
                                        <option value="">— тег —</option>
                                        {WALL_TAGS.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                                      </select>
                                    </div>
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-1">
                                        <span className="text-gray-400 text-[10px]">Товщ:</span>
                                        <input type="number" value={zone.edgeThickness?.[eIdx] || ''} placeholder="100"
                                          onClick={(e) => e.stopPropagation()}
                                          onChange={(e) => { e.stopPropagation(); setZones(zones.map(z => z.id === zone.id ? { ...z, edgeThickness: { ...(z.edgeThickness || {}), [eIdx]: parseInt(e.target.value) || 0 } } : z)); }}
                                          className="w-12 border rounded px-1 py-0.5 text-[10px] text-right" />
                                        <span className="text-gray-300 text-[10px]">мм</span>
                                      </div>
                                      {edge.wallArea && (
                                        <span className="text-[10px]">
                                          <span className="text-gray-400">{edge.wallArea} м²</span>
                                          {edge.openingsArea && <span className="text-gray-300 ml-1">(-{edge.openingsArea})</span>}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  {/* Openings */}
                                  {(() => {
                                    const edgeOpenings = (zone.openings || []).filter(o => o.edgeLabel === edge.label);
                                    return (
                                      <div className="mt-1 pt-1 border-t border-dashed space-y-0.5">
                                        {edgeOpenings.map((opening, oIdx) => (
                                          <div key={oIdx} className="flex items-center justify-between">
                                            <span className="text-[10px]" style={{ color: BRAND.navy }}>
                                              {opening.type === 'door' ? 'Двері' : opening.type === 'window' ? 'Вікно' : 'Ніша'}: {opening.widthMm}×{opening.heightMm}
                                            </span>
                                            <button className="text-gray-300 hover:text-red-400" onClick={(e) => { e.stopPropagation(); setZones(zones.map(z => z.id !== zone.id ? z : { ...z, openings: (z.openings || []).filter(o => o !== opening) })); }}>
                                              <X className="w-2.5 h-2.5" />
                                            </button>
                                          </div>
                                        ))}
                                        <button className="w-full text-[10px] text-center py-0.5 border rounded hover:bg-gray-50 text-gray-400"
                                          onClick={(e) => { e.stopPropagation(); setShowOpeningForm({ target: 'zone', id: zone.id, edgeLabel: edge.label, type: 'door', widthMm: 900, heightMm: 2100 }); }}>
                                          + Отвір
                                        </button>
                                      </div>
                                    );
                                  })()}
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            {zones.length > 0 && (
              <div className="px-4 py-3 border-t" style={{ backgroundColor: BRAND.navy + '08' }}>
                <div className="text-xs text-gray-500 mb-1">Загальна площа</div>
                <div className="font-bold text-xl" style={{ color: BRAND.navy }}>
                  {zones.reduce((s, z) => s + calcArea(z.points), 0).toFixed(2)} м²
                </div>
              </div>
            )}

            {/* Walls section */}
            {walls.length > 0 && (
              <>
                <div className="px-3 py-2 border-t" style={{ backgroundColor: BRAND.navy }}>
                  <h3 className="font-semibold text-xs text-white">Стіни</h3>
                  <p className="text-[10px] text-white/50 mt-0.5">
                    Д: {walls.filter(w => w.tag === 'demolition').reduce((s, w) => { const l = ppmBase > 0 ? Math.sqrt((w.p2.x - w.p1.x) ** 2 + (w.p2.y - w.p1.y) ** 2) / ppmBase : 0; const h = w.slopedWall ? ((w.heightP1Mm || 0) + (w.heightP2Mm || 0)) / 2 : w.heightMm; return s + l * h / 1000; }, 0).toFixed(2)} м²
                    {' | '}
                    М: {walls.filter(w => w.tag === 'construction').reduce((s, w) => { const l = ppmBase > 0 ? Math.sqrt((w.p2.x - w.p1.x) ** 2 + (w.p2.y - w.p1.y) ** 2) / ppmBase : 0; const h = w.slopedWall ? ((w.heightP1Mm || 0) + (w.heightP2Mm || 0)) / 2 : w.heightMm; return s + l * h / 1000; }, 0).toFixed(2)} м²
                  </p>
                </div>
                {walls.map(wall => {
                  const tag = WALL_TAGS.find(t => t.id === wall.tag) || WALL_TAGS[1];
                  const lenMm = ppmBase > 0 ? Math.round(Math.sqrt((wall.p2.x - wall.p1.x) ** 2 + (wall.p2.y - wall.p1.y) ** 2) / ppmBase * 1000) : 0;
                  const effH = wall.slopedWall ? ((wall.heightP1Mm || 0) + (wall.heightP2Mm || 0)) / 2 : wall.heightMm;
                  const grossArea = (lenMm * effH) / 1000000;
                  const openingsArea = (wall.openings || []).reduce((s, o) => s + (o.widthMm * o.heightMm) / 1000000, 0);
                  const netArea = Math.max(0, grossArea - openingsArea);
                  const isSel = wall.id === selectedWallId;
                  return (
                    <div key={wall.id}
                      className={`px-3 py-2 border-b cursor-pointer transition-colors ${isSel ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                      onClick={() => { setSelectedWallId(wall.id); setSelectedZoneId(null); }}>
                      {/* Header */}
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-1.5">
                          <div className="w-2.5 h-2.5 rounded" style={{ backgroundColor: tag.color }} />
                          <span className="text-xs font-semibold" style={{ color: BRAND.navy }}>{lenMm} мм</span>
                          {wall.material && <span className="text-[10px] text-gray-400">{wall.material}</span>}
                        </div>
                        <button onClick={(e) => { e.stopPropagation(); setWalls(walls.filter(w => w.id !== wall.id)); if (isSel) setSelectedWallId(null); }}
                          className="p-0.5 text-gray-300 hover:text-red-500"><Trash2 className="w-3 h-3" /></button>
                      </div>

                      {/* Details when selected */}
                      {isSel && (
                        <div className="space-y-1.5 text-[11px]">
                          {/* Tag toggle */}
                          <div className="flex gap-1">
                            {WALL_TAGS.map(t => (
                              <button key={t.id}
                                className={`flex-1 px-2 py-1 rounded text-[10px] border transition-colors ${wall.tag === t.id ? 'text-white' : 'text-gray-500 hover:bg-gray-50'}`}
                                style={wall.tag === t.id ? { backgroundColor: t.color, borderColor: t.color } : {}}
                                onClick={(e) => { e.stopPropagation(); setWalls(walls.map(w => w.id === wall.id ? { ...w, tag: t.id } : w)); }}
                              >{t.label}</button>
                            ))}
                          </div>
                          {/* Length */}
                          <div className="flex items-center justify-between">
                            <span className="text-gray-400">Довжина:</span>
                            <div className="flex items-center gap-0.5">
                              <input type="number" value={lenMm}
                                className="w-16 border rounded px-1 py-0.5 text-[11px] text-right"
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => {
                                  const newMm = parseInt(e.target.value) || 0;
                                  const dx = wall.p2.x - wall.p1.x, dy = wall.p2.y - wall.p1.y;
                                  const curLen = Math.sqrt(dx * dx + dy * dy);
                                  if (curLen === 0) return;
                                  const cx = (wall.p1.x + wall.p2.x) / 2, cy = (wall.p1.y + wall.p2.y) / 2;
                                  const halfNew = (newMm / 1000 * ppmBase) / 2;
                                  const ux = dx / curLen, uy = dy / curLen;
                                  setWalls(walls.map(w => w.id === wall.id ? { ...w, p1: { x: cx - ux * halfNew, y: cy - uy * halfNew }, p2: { x: cx + ux * halfNew, y: cy + uy * halfNew } } : w));
                                }} />
                              <span className="text-gray-300">мм</span>
                            </div>
                          </div>
                          {/* Height */}
                          {!wall.slopedWall ? (
                            <div className="flex items-center justify-between">
                              <span className="text-gray-400">Висота:</span>
                              <div className="flex items-center gap-0.5">
                                <input type="number" value={wall.heightMm}
                                  className="w-16 border rounded px-1 py-0.5 text-[11px] text-right"
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={(e) => { const v = parseInt(e.target.value) || 0; setWalls(walls.map(w => w.id === wall.id ? { ...w, heightMm: v, heightP1Mm: v, heightP2Mm: v } : w)); }} />
                                <span className="text-gray-300">мм</span>
                              </div>
                            </div>
                          ) : (
                            <div className="space-y-0.5">
                              <div className="flex items-center justify-between">
                                <span className="text-gray-400">Висота P1:</span>
                                <div className="flex items-center gap-0.5">
                                  <input type="number" value={wall.heightP1Mm || ''}
                                    className="w-16 border rounded px-1 py-0.5 text-[11px] text-right"
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => setWalls(walls.map(w => w.id === wall.id ? { ...w, heightP1Mm: parseInt(e.target.value) || 0 } : w))} />
                                  <span className="text-gray-300">мм</span>
                                </div>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-gray-400">Висота P2:</span>
                                <div className="flex items-center gap-0.5">
                                  <input type="number" value={wall.heightP2Mm || ''}
                                    className="w-16 border rounded px-1 py-0.5 text-[11px] text-right"
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => setWalls(walls.map(w => w.id === wall.id ? { ...w, heightP2Mm: parseInt(e.target.value) || 0 } : w))} />
                                  <span className="text-gray-300">мм</span>
                                </div>
                              </div>
                            </div>
                          )}
                          <label className="flex items-center gap-1.5 cursor-pointer" onClick={(e) => e.stopPropagation()}>
                            <input type="checkbox" checked={wall.slopedWall || false}
                              onChange={(e) => setWalls(walls.map(w => w.id === wall.id ? { ...w, slopedWall: e.target.checked } : w))}
                              className="w-3 h-3" style={{ accentColor: BRAND.navy }} />
                            <span className="text-[10px] text-gray-400">Скошена стіна</span>
                          </label>
                          {/* Material */}
                          <div className="flex items-center justify-between">
                            <span className="text-gray-400">Матеріал:</span>
                            <select value={wall.material}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => { e.stopPropagation(); setWalls(walls.map(w => w.id === wall.id ? { ...w, material: e.target.value } : w)); }}
                              className="text-[10px] border rounded px-1 py-0.5">
                              <option value="">—</option>
                              {WALL_MATERIALS.map(m => <option key={m} value={m}>{m}</option>)}
                            </select>
                          </div>
                          {/* Thickness */}
                          <div className="flex items-center justify-between">
                            <span className="text-gray-400">Товщина:</span>
                            <div className="flex items-center gap-0.5">
                              <input type="number" value={wall.thicknessMm || ''}
                                placeholder="0"
                                className="w-16 border rounded px-1 py-0.5 text-[11px] text-right"
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => setWalls(walls.map(w => w.id === wall.id ? { ...w, thicknessMm: parseInt(e.target.value) || 0 } : w))} />
                              <span className="text-gray-300">мм</span>
                            </div>
                          </div>
                          {/* Area */}
                          <div className="flex items-center justify-between">
                            <span className="text-gray-400">Площа:</span>
                            <span className="font-semibold" style={{ color: BRAND.navy }}>
                              {netArea.toFixed(2)} м²
                              {openingsArea > 0 && <span className="text-gray-300 font-normal ml-1">(-{openingsArea.toFixed(2)})</span>}
                            </span>
                          </div>
                          {/* Openings */}
                          {(wall.openings || []).map((op, oIdx) => (
                            <div key={oIdx} className="flex items-center justify-between pl-2 border-l-2" style={{ borderColor: op.type === 'door' ? '#22c55e' : '#3b82f6' }}>
                              <span className="text-gray-500">{op.type === 'door' ? 'Двері' : op.type === 'window' ? 'Вікно' : 'Ніша'}: {op.widthMm}×{op.heightMm}</span>
                              <button className="text-gray-300 hover:text-red-400"
                                onClick={(e) => { e.stopPropagation(); setWalls(walls.map(w => w.id === wall.id ? { ...w, openings: w.openings.filter((_, i) => i !== oIdx) } : w)); }}>
                                <X className="w-2.5 h-2.5" />
                              </button>
                            </div>
                          ))}
                          <button className="w-full text-[10px] text-center py-1 border rounded hover:bg-gray-50 text-gray-500"
                            onClick={(e) => { e.stopPropagation(); setShowWallOpeningForm({ wallId: wall.id }); }}>
                            + Отвір
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </>
            )}

            {/* Wall form — create new or add opening */}
            {showWallOpeningForm && (
              <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-50" onClick={() => setShowWallOpeningForm(null)}>
                <div className="bg-white rounded-xl shadow-lg p-6 border" style={{ borderColor: BRAND.navy, minWidth: 320 }} onClick={(e) => e.stopPropagation()}>
                  <h3 className="font-bold text-base mb-4" style={{ color: BRAND.navy }}>
                    {showWallOpeningForm.isNew ? 'Нова стіна' : 'Додати отвір'}
                  </h3>
                  <div className="space-y-3">
                    {/* New wall fields */}
                    {showWallOpeningForm.isNew && (
                      <>
                        {/* Tag */}
                        <div>
                          <span className="text-xs text-gray-500 block mb-1">Тип стіни</span>
                          <div className="flex gap-1">
                            {WALL_TAGS.map(t => (
                              <button key={t.id}
                                className={`flex-1 px-2 py-2 text-xs rounded border-2 transition-colors font-medium ${showWallOpeningForm.tag === t.id ? 'text-white' : 'text-gray-500 hover:bg-gray-50'}`}
                                style={showWallOpeningForm.tag === t.id ? { backgroundColor: t.color, borderColor: t.color } : {}}
                                onClick={() => setShowWallOpeningForm({ ...showWallOpeningForm, tag: t.id })}
                              >{t.label}</button>
                            ))}
                          </div>
                        </div>
                        {/* Height */}
                        {!showWallOpeningForm.slopedWall ? (
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-500">Висота</span>
                            <div className="flex items-center gap-1">
                              <input type="number" value={showWallOpeningForm.heightMm || 2700}
                                onChange={(e) => { const v = parseInt(e.target.value) || 0; setShowWallOpeningForm({ ...showWallOpeningForm, heightMm: v, heightP1Mm: v, heightP2Mm: v }); }}
                                className="w-24 border-2 rounded-lg px-3 py-1.5 text-sm text-right" style={{ borderColor: BRAND.navy + '30' }} />
                              <span className="text-gray-400 text-sm">мм</span>
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-1.5">
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-gray-500">Висота P1</span>
                              <div className="flex items-center gap-1">
                                <input type="number" value={showWallOpeningForm.heightP1Mm || 2700}
                                  onChange={(e) => setShowWallOpeningForm({ ...showWallOpeningForm, heightP1Mm: parseInt(e.target.value) || 0 })}
                                  className="w-24 border-2 rounded-lg px-3 py-1.5 text-sm text-right" style={{ borderColor: BRAND.navy + '30' }} />
                                <span className="text-gray-400 text-sm">мм</span>
                              </div>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-gray-500">Висота P2</span>
                              <div className="flex items-center gap-1">
                                <input type="number" value={showWallOpeningForm.heightP2Mm || 2700}
                                  onChange={(e) => setShowWallOpeningForm({ ...showWallOpeningForm, heightP2Mm: parseInt(e.target.value) || 0 })}
                                  className="w-24 border-2 rounded-lg px-3 py-1.5 text-sm text-right" style={{ borderColor: BRAND.navy + '30' }} />
                                <span className="text-gray-400 text-sm">мм</span>
                              </div>
                            </div>
                          </div>
                        )}
                        {/* Sloped wall toggle */}
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" checked={showWallOpeningForm.slopedWall || false}
                            onChange={(e) => setShowWallOpeningForm({ ...showWallOpeningForm, slopedWall: e.target.checked })}
                            className="w-3.5 h-3.5" style={{ accentColor: BRAND.navy }} />
                          <span className="text-xs text-gray-500">Скошена стіна</span>
                        </label>
                        {/* Material */}
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500">Матеріал</span>
                          <select value={showWallOpeningForm.material || ''}
                            onChange={(e) => setShowWallOpeningForm({ ...showWallOpeningForm, material: e.target.value })}
                            className="border-2 rounded-lg px-2 py-1.5 text-sm" style={{ borderColor: BRAND.navy + '30' }}>
                            <option value="">— оберіть —</option>
                            {WALL_MATERIALS.map(m => <option key={m} value={m}>{m}</option>)}
                          </select>
                        </div>
                        {/* Thickness */}
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500">Товщина</span>
                          <div className="flex items-center gap-1">
                            <input type="number" value={showWallOpeningForm.thicknessMm || 100}
                              onChange={(e) => setShowWallOpeningForm({ ...showWallOpeningForm, thicknessMm: parseInt(e.target.value) || 0 })}
                              className="w-24 border-2 rounded-lg px-3 py-1.5 text-sm text-right" style={{ borderColor: BRAND.navy + '30' }} />
                            <span className="text-gray-400 text-sm">мм</span>
                          </div>
                        </div>
                      </>
                    )}
                    {/* Opening fields (for adding to existing wall) */}
                    {!showWallOpeningForm.isNew && (
                      <>
                        <div className="flex gap-1">
                          {OPENING_TYPES.map(t => (
                            <button key={t.id}
                              className={`flex-1 px-2 py-1.5 text-xs rounded border transition-colors ${(showWallOpeningForm.type || 'door') === t.id ? 'text-white' : 'text-gray-600 hover:bg-gray-50'}`}
                              style={(showWallOpeningForm.type || 'door') === t.id ? { backgroundColor: BRAND.navy, borderColor: BRAND.navy } : {}}
                              onClick={() => setShowWallOpeningForm({ ...showWallOpeningForm, type: t.id })}
                            >{t.label}</button>
                          ))}
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-gray-500">Ширина:</span>
                          <div className="flex items-center gap-1">
                            <input type="number" value={showWallOpeningForm.widthMm || 900}
                              onChange={(e) => setShowWallOpeningForm({ ...showWallOpeningForm, widthMm: parseInt(e.target.value) || 0 })}
                              className="w-20 border rounded px-2 py-1 text-right" /><span className="text-gray-400">мм</span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-gray-500">Висота:</span>
                          <div className="flex items-center gap-1">
                            <input type="number" value={showWallOpeningForm.openingHeightMm || 2100}
                              onChange={(e) => setShowWallOpeningForm({ ...showWallOpeningForm, openingHeightMm: parseInt(e.target.value) || 0 })}
                              className="w-20 border rounded px-2 py-1 text-right" /><span className="text-gray-400">мм</span>
                          </div>
                        </div>
                      </>
                    )}
                    {/* Actions */}
                    <div className="flex gap-2 pt-2">
                      <button className="flex-1 px-4 py-2 border-2 rounded-lg text-sm text-gray-600 hover:bg-gray-50"
                        onClick={() => setShowWallOpeningForm(null)}>Скасувати</button>
                      <button className="flex-1 px-4 py-2 text-white rounded-lg text-sm font-medium"
                        style={{ backgroundColor: BRAND.navy }}
                        onClick={() => {
                          if (showWallOpeningForm.isNew) {
                            // Create new wall
                            const { p1, p2, tag, heightMm, slopedWall, heightP1Mm, heightP2Mm, material, thicknessMm } = showWallOpeningForm;
                            const newWall = {
                              id: Date.now(), page: pageNum, p1, p2,
                              tag: tag || 'existing', heightMm: heightMm || 2700,
                              slopedWall: slopedWall || false,
                              heightP1Mm: slopedWall ? (heightP1Mm || 2700) : (heightMm || 2700),
                              heightP2Mm: slopedWall ? (heightP2Mm || 2700) : (heightMm || 2700),
                              material: material || '', thicknessMm: thicknessMm || 100,
                              side: 1, openings: [],
                            };
                            setWalls(prev => [...prev, newWall]);
                            setSelectedWallId(newWall.id);
                          } else {
                            // Add opening to existing wall
                            const { wallId, type, widthMm, openingHeightMm } = showWallOpeningForm;
                            const op = { type: type || 'door', widthMm: widthMm || 900, heightMm: openingHeightMm || 2100 };
                            setWalls(walls.map(w => w.id === wallId ? { ...w, openings: [...(w.openings || []), op] } : w));
                          }
                          setShowWallOpeningForm(null);
                        }}>{showWallOpeningForm.isNew ? 'Створити' : 'Додати'}</button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Ruler notes — editable */}
            {rulerNotes.length > 0 && (
              <>
                <div className="px-4 py-2 border-t" style={{ backgroundColor: BRAND.navy + '08' }}>
                  <span className="text-xs font-semibold" style={{ color: BRAND.navy }}>Виміри</span>
                </div>
                {rulerNotes.map(n => (
                  <div key={n.id} className="px-3 py-2 border-b">
                    {/* Editable name */}
                    <div className="flex items-center justify-between mb-1">
                      <input
                        type="text"
                        value={n.name}
                        placeholder="Без назви"
                        onChange={(e) => setRulerNotes(rulerNotes.map(r => r.id === n.id ? { ...r, name: e.target.value } : r))}
                        className="text-xs font-medium bg-transparent border-none outline-none flex-1 placeholder-gray-300"
                        style={{ color: BRAND.navy }}
                      />
                      <button onClick={() => setRulerNotes(rulerNotes.filter(r => r.id !== n.id))} className="text-gray-300 hover:text-red-500 ml-1 flex-shrink-0">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                    {/* Values with delete buttons */}
                    <div className="space-y-0.5">
                      {n.value && (
                        <div className="flex items-center justify-between text-[11px]">
                          <span className="text-gray-500">Довжина: <strong style={{ color: BRAND.navy }}>{n.value} м</strong></span>
                          <button onClick={() => setRulerNotes(rulerNotes.map(r => r.id === n.id ? { ...r, value: null } : r))} className="text-gray-200 hover:text-red-400"><X className="w-2.5 h-2.5" /></button>
                        </div>
                      )}
                      {n.perimeter && (
                        <div className="flex items-center justify-between text-[11px]">
                          <span className="text-gray-500">Периметр: <strong style={{ color: BRAND.navy }}>{n.perimeter} м</strong></span>
                          <button onClick={() => setRulerNotes(rulerNotes.map(r => r.id === n.id ? { ...r, perimeter: null } : r))} className="text-gray-200 hover:text-red-400"><X className="w-2.5 h-2.5" /></button>
                        </div>
                      )}
                      {n.area && (
                        <div className="flex items-center justify-between text-[11px]">
                          <span className="text-gray-500">Площа: <strong style={{ color: BRAND.navy }}>{n.area} м²</strong></span>
                          <button onClick={() => setRulerNotes(rulerNotes.map(r => r.id === n.id ? { ...r, area: null } : r))} className="text-gray-200 hover:text-red-400"><X className="w-2.5 h-2.5" /></button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ToolBtn({ icon: Icon, label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded transition-colors ${active ? 'text-white' : 'text-gray-600 hover:bg-gray-100'}`}
      style={active ? { backgroundColor: BRAND.navy } : {}}
      title={label}
    >
      <Icon className="w-3.5 h-3.5" />
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}
